/* ═══════════════════════════════════════════════════════════════════
   BIN IBRAHIM SHOPIFY THEME — FULL JAVASCRIPT ENGINE
   Converted from static site to Shopify OS 2.0 compatible.
   - Three.js WebGL Galaxy
   - Lenis Smooth Scrolling
   build: 2026-07-29-related-carousel-fix
   - All carousels, drawers, overlays
   - Shopify Cart AJAX API
   - Wishlist (localStorage)
   - Product page interactions
   - Collections filter/sort
   - Video sections (3D Coverflow, Split, Floating, Fullwidth)
   ═══════════════════════════════════════════════════════════════════ */

'use strict';

/* ── PREMIUM VIDEO SECTIONS CONFIG ──
   Override via theme section settings data attributes on the containers */
window.premiumVideoSectionsConfig = {
  coverflow: {
    autoplaySpeed: 4000,
    items: []  // Populated from section data attributes
  },
  splitShowcase: {},
  floatingCards: [],
  fullWidthBanner: {}
};

/* Fix stale/blank carousels after a browser back/forward navigation.
   The page is restored from the bfcache frozen mid-animation and
   DOMContentLoaded never re-fires, which leaves the category circles
   (and other sliders) blank. Reloading guarantees a clean init. */
window.addEventListener('pageshow', (e) => {
  if (e.persisted) window.location.reload();
});

/* ── THREE.JS GALAXY ENGINE ── */
let scene, camera, renderer, starPoints;

const starVertexShader = `
  attribute float size;
  attribute vec3 color;
  varying vec3 vColor;
  varying float vOpacity;
  uniform float uTime;

  void main() {
    vColor = color;
    vOpacity = 0.78 + 0.22 * sin(uTime * 1.2 + position.x * 127.5 + position.y * 193.7);
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = size * (400.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const starFragmentShader = `
  varying vec3 vColor;
  varying float vOpacity;
  void main() {
    float dist = distance(gl_PointCoord, vec2(0.5));
    float alpha = smoothstep(0.5, 0.15, dist);
    float glow = 0.04 / (dist + 0.005);
    gl_FragColor = vec4(vColor + (glow * 0.08), vOpacity * alpha);
  }
`;

let scrollRotationY = 0;
let targetScrollRotationY = 0;
let galaxyVisible = true;
let parallaxItems = [];

document.addEventListener('visibilitychange', () => {
  galaxyVisible = !document.hidden;
}, { passive: true });

function initGalaxy() {
  const canvas = document.getElementById('glx-canvas');
  if (!canvas || typeof THREE === 'undefined') return;

  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(65, window.innerWidth / window.innerHeight, 0.1, 1000);
  camera.position.set(0, 0, 30);

  const isMobile = window.innerWidth < 768;

  renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false,
    alpha: true,
    powerPreference: 'high-performance'
  });
  renderer.setSize(window.innerWidth, window.innerHeight);
  // Zero-lag pixel ratio: maximum 1.0 on desktop, 0.65 on mobile = silky 60fps
  renderer.setPixelRatio(isMobile ? 0.65 : Math.min(window.devicePixelRatio, 1.0));

  const numStars = isMobile ? 250 : 800;
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(numStars * 3);
  const colors = new Float32Array(numStars * 3);
  const sizes = new Float32Array(numStars);

  for (let i = 0; i < numStars; i++) {
    const radius = 30 + Math.random() * (isMobile ? 60 : 100);
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);

    positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
    positions[i * 3 + 2] = radius * Math.cos(phi);

    /* Stars: white only, with a rare soft-gold accent — no rainbow colors */
    const colorMix = Math.random();
    if (colorMix > 0.9) {
      colors[i * 3] = 1.0; colors[i * 3 + 1] = 0.92; colors[i * 3 + 2] = 0.72;
    } else {
      colors[i * 3] = 1.0; colors[i * 3 + 1] = 1.0; colors[i * 3 + 2] = 1.0;
    }

    /* Small, delicate stars — fine speckled field, consistent site-wide */
    sizes[i] = Math.random() * 0.45 + (isMobile ? 0.18 : 0.12);
  }

  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
  geometry.computeBoundingSphere();

  const material = new THREE.ShaderMaterial({
    uniforms: { uTime: { value: 0 } },
    vertexShader: starVertexShader,
    fragmentShader: starFragmentShader,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false
  });

  starPoints = new THREE.Points(geometry, material);
  starPoints.frustumCulled = true;
  scene.add(starPoints);

  animateGalaxy();
}

let targetRotationX = 0;
let targetRotationY = 0;
let currentRotationX = 0;
let currentRotationY = 0;

let mouseMovePending = false;
window.addEventListener('mousemove', (e) => {
  if (window.innerWidth < 768) return; /* desktop-only steering */
  if (mouseMovePending) return;
  mouseMovePending = true;
  requestAnimationFrame(() => {
    targetRotationY = ((e.clientX / window.innerWidth) - 0.5) * 0.6;
    targetRotationX = ((e.clientY / window.innerHeight) - 0.5) * 0.4;
    mouseMovePending = false;
  });
}, { passive: true });

/* Mobile: galaxy does NOT react to touch — it drifts on its own and
   rotates with page scroll (handled in handleGlobalScroll). Only desktop
   mouse movement steers it via the mousemove listener above. */

let lastScrolledVal = false;
let scrollTicking = false;

// DOM Cache for performance-critical scroll handlers
let cachedSiteHeader = null;
let cachedFullBgVideo = null;
let cachedFullVideoContainer = null;

function getScrollElements() {
  if (!cachedSiteHeader) cachedSiteHeader = document.querySelector('.site-header');
  if (!cachedFullBgVideo) cachedFullBgVideo = document.querySelector('.fullwidth-bg-video');
  if (!cachedFullVideoContainer) cachedFullVideoContainer = document.getElementById('fullwidth-video-container');
}

// Detect touch/small screens once — parallax + heavy scroll effects are the
// main cause of scroll jank there, so we disable them on mobile.
var _lowPowerScroll = window.matchMedia('(hover: none)').matches || window.innerWidth <= 768;

function handleGlobalScroll(scrollY) {
  getScrollElements();

  // Header scrolled-state is cheap — always keep it.
  if (cachedSiteHeader) {
    const scrolledState = scrollY > 30;
    if (scrolledState !== lastScrolledVal) {
      cachedSiteHeader.classList.toggle('is-scrolled', scrolledState);
      lastScrolledVal = scrolledState;
    }
  }

  // Galaxy scroll-rotation is cheap (just updates a target number that the
  // galaxy's own rAF loop eases toward) — keep it on ALL devices so the
  // starfield reacts to scrolling everywhere.
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
  const progress = maxScroll > 0 ? scrollY / maxScroll : 0;
  targetScrollRotationY = progress * 1.2;

  // Parallax transforms are DISABLED — they moved sections up/down as you
  // scrolled, which looked like sections "jumping/collapsing" and stuttered
  // on fast scroll. A stable page scrolls far smoother than a parallax one.
}

var _isPageScrolling = false;
var _scrollStopTimer = null;

window.addEventListener('scroll', () => {
  _isPageScrolling = true;
  clearTimeout(_scrollStopTimer);
  _scrollStopTimer = setTimeout(() => { _isPageScrolling = false; }, 80);

  if (!scrollTicking) {
    requestAnimationFrame(() => {
      handleGlobalScroll(window.scrollY);
      scrollTicking = false;
    });
    scrollTicking = true;
  }
}, { passive: true });

var _galaxyLastFrame = 0;
function animateGalaxy() {
  requestAnimationFrame(animateGalaxy);
  if (!galaxyVisible || !starPoints) return;
  if (document.hidden) return;
  
  // During active scroll, throttle WebGL to 25fps so DOM compositor gets 100% priority (silky 60-120fps)
  var _now = performance.now();
  var _frameInterval = (_lowPowerScroll || _isPageScrolling) ? 40 : 22;
  if (_now - _galaxyLastFrame < _frameInterval) return;
  _galaxyLastFrame = _now;

  const time = performance.now() * 0.001;
  starPoints.material.uniforms.uTime.value = time;

  currentRotationX += (targetRotationX - currentRotationX) * 0.06;
  currentRotationY += (targetRotationY - currentRotationY) * 0.06;
  // Faster ease so the galaxy reacts snappily to scrolling.
  scrollRotationY += (targetScrollRotationY - scrollRotationY) * 0.12;

  const autoX = Math.sin(time * 0.015) * 0.3;
  const autoY = time * 0.008;

  starPoints.rotation.x = autoX + currentRotationX;
  starPoints.rotation.y = autoY + currentRotationY + scrollRotationY;
  starPoints.rotation.z = time * 0.003;

  renderer.render(scene, camera);
}

window.addEventListener('beforeunload', () => {
  if (renderer) renderer.dispose();
  if (starPoints) {
    starPoints.geometry.dispose();
    starPoints.material.dispose();
  }
});

window.addEventListener('resize', () => {
  if (!camera || !renderer) return;
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
});

/* ═══════════════════════════════════════
   SHOPIFY CART API
   ═══════════════════════════════════════ */
const ShopifyCart = {
  async getCart() {
    try {
      const resp = await fetch('/cart.js');
      return await resp.json();
    } catch (e) {
      return null;
    }
  },

  async addItem(variantId, quantity = 1, properties = {}) {
    try {
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity, properties })
      });
      const data = await resp.json();
      if (data.status === 422 || data.description) {
        throw new Error(data.description || 'Cannot add item');
      }
      return data;
    } catch (e) {
      throw e;
    }
  },

  async updateItem(key, quantity) {
    try {
      const resp = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      return await resp.json();
    } catch (e) {
      return null;
    }
  },

  async removeItem(key) {
    return this.updateItem(key, 0);
  },

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    // Keep the .00 by enforcing minimumFractionDigits: 2 in toLocaleString
    return window.Shopify?.currency?.active === 'PKR'
      ? `Rs ${parseFloat(amount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
      : `Rs ${amount}`;
  }
};

/* ── Cart Count Update ── */
async function updateCartCount(cartData = null) {
  const cart = cartData || await ShopifyCart.getCart();
  if (!cart) return;
  const counts = document.querySelectorAll('[data-cart-count]');
  counts.forEach(c => c.textContent = cart.item_count > 0 ? cart.item_count : '');
}

/* ── "Added to Cart" toast (same style as the wishlist toast) ──
   Replaces the old behaviour of sliding the cart drawer open on every add. */
function biCartToast(message) {
  let toast = document.getElementById('cart-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'cart-toast';
    Object.assign(toast.style, {
      position: 'fixed', bottom: window.innerWidth <= 768 ? '80px' : '30px', left: '50%',
      transform: 'translateX(-50%) translateY(40px)',
      opacity: '0', visibility: 'hidden',
      background: 'rgba(6,6,12,0.96)', color: '#fff',
      border: '1px solid rgba(212,175,55,0.5)', borderRadius: '30px',
      padding: '12px 22px', fontSize: '0.82rem', fontWeight: '600',
      display: 'flex', alignItems: 'center', gap: '8px',
      zIndex: '99999',
      transition: 'transform 0.3s cubic-bezier(0.22,1,0.36,1), opacity 0.3s ease, visibility 0.3s',
      boxShadow: '0 10px 30px rgba(0,0,0,0.5)', whiteSpace: 'nowrap'
    });
    document.body.appendChild(toast);
  }
  toast.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" stroke-width="2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg> ' + (message || 'Added to Cart!');
  requestAnimationFrame(() => {
    toast.style.visibility = 'visible';
    toast.style.opacity = '1';
    toast.style.transform = 'translateX(-50%) translateY(0)';
  });
  clearTimeout(toast._hideT);
  toast._hideT = setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.visibility = 'hidden';
    toast.style.transform = 'translateX(-50%) translateY(40px)';
  }, 2200);
}

/* ── Cart Drawer Render ── */
async function renderCartDrawer(cartData = null) {
  const list = document.getElementById('cart-drawer-list');
  const totalEl = document.getElementById('cart-drawer-total');
  const checkoutBtn = document.getElementById('cart-checkout-btn');
  if (!list) return;

  let cart = cartData;
  if (!cart) {
    list.innerHTML = '<div class="cart-loading-state"><div class="cart-loading-spinner"></div></div>';
    if (checkoutBtn) checkoutBtn.style.opacity = '0.5';
    cart = await ShopifyCart.getCart();
  }

  if (!cart || cart.items.length === 0) {
    list.innerHTML = `<div style="text-align:center; padding:40px 10px; color:var(--muted); font-size:0.95rem;">Your bag is empty.</div>`;
    if (totalEl) totalEl.textContent = ShopifyCart.formatMoney(0);
    if (checkoutBtn) checkoutBtn.style.opacity = '0.5';
    // Clear counts
    const counts = document.querySelectorAll('[data-cart-count]');
    counts.forEach(c => c.textContent = '');
    return;
  }

  if (checkoutBtn) checkoutBtn.style.opacity = '1';

  let html = '';
  cart.items.forEach(item => {
    html += `
      <div class="cart-item" data-key="${item.key}">
        <img src="${item.featured_image?.url || item.image}" class="cart-item-img" alt="${item.product_title}" fetchpriority="high">
        <div class="cart-item-info" style="flex:1;">
          <h4>${item.product_title}</h4>
          <p style="font-size:0.75rem; color:var(--muted); margin-bottom:6px;">${item.variant_title !== 'Default Title' ? item.variant_title : ''}</p>
          <div style="display:flex; justify-content:space-between; align-items:center;">
            <div class="quantity-adjuster" style="transform-origin:left; scale:0.8;">
              <button class="cart-qty-btn" data-key="${item.key}" data-action="dec">−</button>
              <input type="number" value="${item.quantity}" readonly style="width:25px;">
              <button class="cart-qty-btn" data-key="${item.key}" data-action="inc">+</button>
            </div>
            <span style="color:var(--gold); font-size:0.8rem;">${ShopifyCart.formatMoney(item.line_price)}</span>
          </div>
        </div>
        <button class="cart-remove-btn" data-key="${item.key}" style="background:transparent;border:none;color:var(--muted);cursor:pointer;font-size:1.1rem;position:absolute;top:0;right:0;">&times;</button>
      </div>
    `;
  });

  list.innerHTML = html;
  if (totalEl) totalEl.textContent = ShopifyCart.formatMoney(cart.total_price);

  // Sync badge counts with this same cart object immediately
  updateCartCount(cart);

  // Bind qty buttons
  // Track pending quantities per line so rapid clicks feel instant and only
  // the final value is sent to the server (debounced) — no lag / queue-up.
  var _pendingQty = {};
  var _qtyTimers = {};
  list.querySelectorAll('.cart-qty-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const key = btn.dataset.key;
      const action = btn.dataset.action;
      const item = cart.items.find(i => i.key === key);
      if (!item) return;

      const base = _pendingQty[key] != null ? _pendingQty[key] : item.quantity;
      const newQty = action === 'inc' ? base + 1 : Math.max(0, base - 1);
      _pendingQty[key] = newQty;

      // Instant visual feedback
      const wrap = btn.closest('.quantity-adjuster') || btn.parentNode;
      const input = wrap && wrap.querySelector('input');
      if (input) input.value = newQty;
      if (newQty === 0) {
        const row = btn.closest('.cart-item');
        if (row) row.style.opacity = '0.4';
      }

      // Debounce the server update (only fires 350ms after the last click)
      clearTimeout(_qtyTimers[key]);
      _qtyTimers[key] = setTimeout(async () => {
        const q = _pendingQty[key];
        delete _pendingQty[key];
        const updatedCart = await ShopifyCart.updateItem(key, q);
        if (updatedCart) renderCartDrawer(updatedCart);
      }, 350);
    });
  });

  list.querySelectorAll('.cart-remove-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const row = btn.closest('.cart-item');
      if (row) row.style.opacity = '0.3';

      const updatedCart = await ShopifyCart.removeItem(btn.dataset.key);
      renderCartDrawer(updatedCart);
    });
  });
}

/* ── Add to Cart with feedback ── */
async function addToCartWithFeedback(variantId, quantity = 1, btn = null) {
  if (btn) {
    btn.disabled = true;
    const original = btn.innerHTML;
    btn.innerHTML = '<div class="cart-loading-spinner" style="width:16px;height:16px;"></div>';

    try {
      await ShopifyCart.addItem(variantId, quantity);

      btn.innerHTML = '✓ Added!';
      btn.style.background = 'rgba(106,191,105,0.2)';
      btn.style.borderColor = '#6abf69';
      btn.style.color = '#6abf69';

      // No drawer — just a clean toast + badge update.
      biCartToast('Added to Cart!');
      updateCartCount();

      setTimeout(() => {
        btn.innerHTML = original;
        btn.style.background = '';
        btn.style.borderColor = '';
        btn.style.color = '';
        btn.disabled = false;
      }, 2000);
    } catch (err) {
      btn.innerHTML = 'Out of Stock';
      btn.style.background = 'rgba(224,140,140,0.2)';
      setTimeout(() => {
        btn.innerHTML = original;
        btn.style.background = '';
        btn.disabled = false;
      }, 2000);
    }
  }
}

/* ═══════════════════════════════════════
   WISHLIST (localStorage — Shopify has no native wishlist)
   ═══════════════════════════════════════ */
const Wishlist = {
  KEY: 'bi_wishlist',

  getAll() {
    try {
      return JSON.parse(localStorage.getItem(this.KEY)) || [];
    } catch (e) {
      return [];
    }
  },

  has(productId) {
    return this.getAll().some(item => item.id === String(productId));
  },

  toggle(product) {
    let list = this.getAll();
    const idx = list.findIndex(item => item.id === String(product.id));
    if (idx > -1) {
      list.splice(idx, 1);
    } else {
      list.push({ id: String(product.id), title: product.title, price: product.price, image: product.image, url: product.url });
    }
    localStorage.setItem(this.KEY, JSON.stringify(list));
    this.updateButtons();
    return idx === -1; // true = added, false = removed
  },

  updateButtons() {
    const list = this.getAll();
    document.querySelectorAll('[data-wishlist-id]').forEach(btn => {
      const id = String(btn.dataset.wishlistId);
      const inList = list.some(item => item.id === id);
      /* Let CSS own the styling of the saved state (filled red heart on the
         PDP, gold on cards). We only toggle the class — no inline SVG fill,
         so section CSS like #wishlistDetailBtn.active svg can win. */
      const svg = btn.querySelector('svg');
      if (svg && !btn.closest('.pdp-icon-btn') && btn.id !== 'wishlistDetailBtn') {
        svg.style.fill = inList ? 'var(--gold)' : 'none';
        svg.style.stroke = inList ? 'var(--gold)' : 'currentColor';
      }
      btn.classList.toggle('active', inList);
    });

    const badges = document.querySelectorAll('#header-wishlist-count, #bottom-wishlist-count');
    badges.forEach(badge => {
      badge.textContent = list.length;
      badge.style.display = list.length > 0 ? 'flex' : 'none';
    });
  },

  getCount() {
    return this.getAll().length;
  }
};

/* ═══════════════════════════════════════
   RECENTLY VIEWED (localStorage)
   ═══════════════════════════════════════ */
const RecentlyViewed = {
  KEY: 'bi_recent_v2',

  track(productId, productData) {
    let list = this.getAll();
    list = list.filter(item => item.id !== String(productId));
    list.unshift({ id: String(productId), ...productData, viewedAt: Date.now() });
    list = list.slice(0, 5);
    try {
      localStorage.setItem(this.KEY, JSON.stringify(list));
    } catch (e) { }
  },

  getAll() {
    try {
      return JSON.parse(localStorage.getItem(this.KEY)) || [];
    } catch (e) {
      return [];
    }
  },

  renderInMenu() {
    const container = document.getElementById('mobile-nav-recent');
    const grid = document.getElementById('mobile-nav-recent-grid');
    if (!container || !grid) return;

    const items = this.getAll();
    if (items.length === 0) {
      container.style.display = 'none';
      return;
    }

    container.style.display = 'block';
    grid.innerHTML = items.slice(0, 2).map(item => `
      <a href="${item.url || '#'}" class="menu-feat-item">
        <img src="${item.image || ''}" alt="${item.title || ''}">
        <div class="menu-feat-info">
          <h5>${item.title || ''}</h5>
          <span class="menu-feat-price">${item.price || ''}</span>
        </div>
      </a>
    `).join('');
  }
};

window.Wishlist = Wishlist;
window.RecentlyViewed = RecentlyViewed;

/* ═══════════════════════════════════════
   DOM READY
   ═══════════════════════════════════════ */
document.addEventListener('DOMContentLoaded', () => {
  /* -- Galaxy init -- */
  if (typeof THREE !== 'undefined') {
    initGalaxy();
  }

  /* -- Dynamic Header Announcement Class Toggle & Scroll State -- */
  function updateHeaderState() {
    const header = document.querySelector('.site-header');
    const announcement = document.querySelector('.announcement-wrapper');
    if (!header) return;

    const annHeight = announcement ? announcement.getBoundingClientRect().height : 0;
    if (annHeight > 0) {
      document.body.classList.add('has-announcement');
    } else {
      document.body.classList.remove('has-announcement');
    }

    // Toggle scrolled state classes immediately on scroll
    if (window.scrollY > 10) {
      header.classList.add('is-scrolled');
      document.body.classList.add('header-is-scrolled');
    } else {
      header.classList.remove('is-scrolled');
      document.body.classList.remove('header-is-scrolled');
    }
  }

  updateHeaderState();
  window.addEventListener('load', updateHeaderState);
  window.addEventListener('resize', updateHeaderState);
  window.addEventListener('scroll', updateHeaderState, { passive: true });
  document.addEventListener('shopify:section:load', updateHeaderState);
  document.addEventListener('shopify:section:unload', updateHeaderState);
  document.addEventListener('shopify:section:select', updateHeaderState);
  document.addEventListener('shopify:section:deselect', updateHeaderState);

  /* -- Unified Lenis Smooth Scroll Engine (Optimized for Laptop & Mouse) -- */
  window.initLenis = function() {
    if (window._lenis || typeof Lenis === 'undefined') return;
    var _isTouch = window.matchMedia('(hover: none)').matches || window.innerWidth <= 768;
    if (_isTouch) return;

    var lenis = new Lenis({
      duration: 1.0,
      easing: function(t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      mouseMultiplier: 0.75,
      wheelMultiplier: 0.75,
      smoothTouch: false,
      touchMultiplier: 1.5,
      infinite: false
    });

    window._lenis = lenis;
    window.__lenis = lenis;

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    if (typeof handleGlobalScroll === 'function') {
      lenis.on('scroll', function(e) { handleGlobalScroll(e.scroll); });
    }
  };

  if (typeof Lenis !== 'undefined') {
    window.initLenis();
  }

  /* ========================================================
     USER ACTIVITY & SCROLL STATE PERSISTENCE ENGINE (Bin Ibrahim)
     Preserves exact scroll position, clicked product card,
     active tabs, and collection states across navigation
     ======================================================== */
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  const BI_STATE_PREFIX = 'bi_state_';
  const biCurrentPath = window.location.pathname;

  // 1. Capture User Actions in Real Time (Click on Products, Tabs, Buttons)
  document.addEventListener('click', function(e) {
    // Capture Product click
    const productLink = e.target.closest('a[href*="/products/"]');
    const productCard = e.target.closest('.pcard, .product-card, [data-product-id]');
    if (productLink || productCard) {
      const pUrl = productLink ? productLink.getAttribute('href') : (productCard.getAttribute('data-product-url') || '');
      const pId = productCard ? (productCard.getAttribute('data-product-id') || '') : '';
      sessionStorage.setItem(BI_STATE_PREFIX + 'last_p_url', pUrl);
      sessionStorage.setItem(BI_STATE_PREFIX + 'last_p_id', pId);
      sessionStorage.setItem(BI_STATE_PREFIX + 'scroll_' + biCurrentPath, window.scrollY || window.pageYOffset || 0);
      sessionStorage.setItem(BI_STATE_PREFIX + 'nav_from', biCurrentPath);
    }

    // Capture Tab Click
    const tabBtn = e.target.closest('.tab-btn, [data-tab]');
    if (tabBtn) {
      const tabId = tabBtn.getAttribute('data-tab') || tabBtn.dataset.tab;
      const section = tabBtn.closest('section') || tabBtn.closest('.tabs-section');
      const secKey = section ? (section.id || 'featured-tabs') : 'featured-tabs';
      if (tabId) {
        sessionStorage.setItem(BI_STATE_PREFIX + 'tab_' + secKey, tabId);
      }
    }

    // Capture View mode click
    const viewBtn = e.target.closest('.view-btn[data-view]');
    if (viewBtn) {
      const viewMode = viewBtn.getAttribute('data-view');
      if (viewMode) {
        localStorage.setItem('bi-collections-view', viewMode);
      }
    }
  }, { capture: true, passive: true });

  // 2. Continuous Scroll Saving (throttled) + pagehide
  let biScrollTimer;
  window.addEventListener('scroll', function() {
    clearTimeout(biScrollTimer);
    biScrollTimer = setTimeout(function() {
      sessionStorage.setItem(BI_STATE_PREFIX + 'scroll_' + biCurrentPath, window.scrollY || window.pageYOffset || 0);
      sessionStorage.setItem('scrollPos_' + biCurrentPath, window.scrollY || window.pageYOffset || 0);
    }, 80);
  }, { passive: true });

  window.addEventListener('pagehide', function() {
    sessionStorage.setItem(BI_STATE_PREFIX + 'scroll_' + biCurrentPath, window.scrollY || window.pageYOffset || 0);
    sessionStorage.setItem('scrollPos_' + biCurrentPath, window.scrollY || window.pageYOffset || 0);
  });

  window.addEventListener('beforeunload', function() {
    sessionStorage.setItem(BI_STATE_PREFIX + 'scroll_' + biCurrentPath, window.scrollY || window.pageYOffset || 0);
    sessionStorage.setItem('scrollPos_' + biCurrentPath, window.scrollY || window.pageYOffset || 0);
  });

  // 3. Robust Multi-stage State & Scroll Restorer
  function restoreUserActivityState() {
    const savedY = sessionStorage.getItem(BI_STATE_PREFIX + 'scroll_' + biCurrentPath) || sessionStorage.getItem('scrollPos_' + biCurrentPath);
    const lastPUrl = sessionStorage.getItem(BI_STATE_PREFIX + 'last_p_url');
    const lastPId = sessionStorage.getItem(BI_STATE_PREFIX + 'last_p_id');

    // A. Restore Active Tabs first so target product card becomes visible
    document.querySelectorAll('.tabs-section, section[id*="tabs"]').forEach(function(sec) {
      const secKey = sec.id || 'featured-tabs';
      const savedTab = sessionStorage.getItem(BI_STATE_PREFIX + 'tab_' + secKey);
      if (savedTab) {
        const btn = sec.querySelector('.tab-btn[data-tab="' + savedTab + '"]');
        if (btn && !btn.classList.contains('active')) {
          btn.click();
        }
      }
    });

    if (!savedY && !lastPUrl && !lastPId) return;

    // Helper to find target product card
    function findTargetCard() {
      let card = null;
      if (lastPUrl) {
        const cleanSlug = lastPUrl.split('?')[0].split('#')[0].replace(/^.*\/products\//, '');
        if (cleanSlug) {
          card = document.querySelector('a[href*="' + cleanSlug + '"]');
          if (card) card = card.closest('.pcard, .product-card, [data-product-id]');
        }
      }
      if (!card && lastPId) {
        card = document.querySelector('[data-product-id="' + lastPId + '"]');
      }
      return card;
    }

    let hasRestoredScroll = false;

    function executeRestoration(attempt) {
      const targetCard = findTargetCard();

      if (targetCard && document.body.contains(targetCard)) {
        // Scroll exactly to target product card
        targetCard.scrollIntoView({ behavior: 'auto', block: 'center' });
        
        if (window._lenis) {
          try {
            window._lenis.scrollTo(targetCard, { offset: -80, immediate: true });
          } catch(e) {}
        }

        // Add subtle gold highlight outline to show user where they returned
        targetCard.style.transition = 'box-shadow 0.4s ease, border-color 0.4s ease';
        targetCard.style.boxShadow = '0 0 25px rgba(212, 175, 55, 0.65)';
        targetCard.style.borderColor = 'rgba(212, 175, 55, 0.9)';
        setTimeout(function() {
          targetCard.style.boxShadow = '';
          targetCard.style.borderColor = '';
        }, 2200);

        hasRestoredScroll = true;
        return;
      }

      // Fallback: restore exact pixel scroll position
      if (savedY) {
        const yNum = parseInt(savedY, 10);
        if (yNum > 0) {
          if (window._lenis) {
            try { window._lenis.scrollTo(yNum, { immediate: true }); } catch(e) {}
          }
          window.scrollTo(0, yNum);
        }
      }

      if (attempt > 0 && !hasRestoredScroll) {
        setTimeout(function() {
          executeRestoration(attempt - 1);
        }, attempt === 4 ? 60 : (attempt === 3 ? 180 : (attempt === 2 ? 400 : 800)));
      }
    }

    // Run progressive restoration across rendering phases
    executeRestoration(4);
  }

  // Hook into lifecycle events
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setTimeout(restoreUserActivityState, 40);
    });
  } else {
    setTimeout(restoreUserActivityState, 40);
  }

  window.addEventListener('pageshow', function() {
    setTimeout(restoreUserActivityState, 60);
  });

  window.addEventListener('load', function() {
    setTimeout(restoreUserActivityState, 120);
  });

  /* -- Progressive image decoding -- */
  document.querySelectorAll('img:not([decoding])').forEach(img => {
    if (!img.classList.contains('preloader-logo') && !img.classList.contains('logo-img')) {
      img.decoding = 'async';
    }
  });
  document.querySelectorAll('video[preload="auto"]').forEach(v => v.setAttribute('preload', 'metadata'));

  /* ── 1. Scroll Reveal Animations ── */
  const observerOptions = {
    root: null,
    rootMargin: '0px 0px -40px 0px',
    threshold: 0.05
  };

  const revealObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('revealed');
        observer.unobserve(entry.target);
      }
    });
  }, observerOptions);
  window.revealObserver = revealObserver;

  /* initScrollReveals() used to tag nearly every section/heading/card on
     the page with .reveal-on-scroll (opacity:0 until an IntersectionObserver
     saw it). Content already in the initial viewport was invisible for a
     beat on every single page load, then popped in — the "jhatka/blink" on
     every navigation. Disabled site-wide; CSS also force-shows any element
     that still ends up with .reveal-on-scroll from elsewhere (product
     cards added dynamically still use window.revealObserver). */

  /* ── 1b. Parallax ── */
  function initPremiumScrollEffects() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const parallaxTargetDefs = [
      { selector: '.slide.active .hero-image', speed: 0.08, direction: 1 },
      { selector: '.fullwidth-bg-video', speed: 0.10, direction: 1 }
    ];

    const parallaxObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const item = parallaxItems.find(i => i.el === entry.target);
        if (item) {
          item.inView = entry.isIntersecting;
        }
      });
    }, { rootMargin: '100px 0px 100px 0px' });

    function cacheParallaxElements() {
      if (parallaxItems && parallaxItems.length) {
        parallaxItems.forEach(item => parallaxObserver.unobserve(item.el));
      }
      parallaxItems = [];
      parallaxTargetDefs.forEach(def => {
        const els = Array.from(document.querySelectorAll(def.selector));
        els.forEach(el => {
          const item = { el, speed: def.speed, direction: def.direction, inView: false };
          parallaxItems.push(item);
          parallaxObserver.observe(el);
        });
      });
    }
    cacheParallaxElements();


    const heroSlider = document.querySelector('.hero-slider');
    if (heroSlider) {
      const slideObserver = new MutationObserver(() => {
        document.querySelectorAll('.hero-image').forEach(img => { img.style.transform = ''; });
        cacheParallaxElements();
      });
      slideObserver.observe(heroSlider, { attributes: true, subtree: true, attributeFilter: ['class'] });
    }

    const sections = document.querySelectorAll('section[id]');
    if (sections.length) {
      const sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            document.body.dataset.activeSection = entry.target.id;
          }
        });
      }, { rootMargin: '-40% 0px -40% 0px', threshold: 0 });
      sections.forEach(s => sectionObserver.observe(s));
    }
  }
  initPremiumScrollEffects();

  /* Page-transition fade-out was removed: it intercepted every link click,
     blacked out the whole page for 350ms (body opacity:0), then navigated —
     felt like a glitch/flash rather than a smooth transition. Links now
     navigate immediately, the browser's native way. */

  /* -- Two-Row Header: Sticky Top Bar scroll class trigger -- */
  const header = document.querySelector('.site-header');
  const headerSection = document.querySelector('.site-header-section');
  const navSection = document.querySelector('.site-navigation-section');

  if (headerSection) {
    headerSection.classList.remove('reveal-on-scroll');
  }

  if (navSection) {
    navSection.classList.add('reveal-on-scroll');
    setTimeout(() => navSection.classList.add('revealed'), 80);
  }



  const heroImgs = document.querySelectorAll('.hero-slider img');
  heroImgs.forEach(img => {
    img.classList.add('reveal-on-scroll');
    setTimeout(() => img.classList.add('revealed'), 150);
  });

  const cards = document.querySelectorAll('.pcard, .promo-banner-card, .trust-card');
  cards.forEach(card => {
    card.classList.add('reveal-on-scroll');
    revealObserver.observe(card);
  });

  document.querySelectorAll('.carousel-track, .promo-grid, .trust-grid').forEach(grid => {
    Array.from(grid.children).forEach((child, index) => {
      child.style.setProperty('--stagger-i', index);
    });
  });

  document.querySelectorAll('.section-head, .slide-caption').forEach(container => {
    Array.from(container.children).forEach((child, index) => {
      child.classList.add('reveal-on-scroll');
      revealObserver.observe(child);
      child.style.setProperty('--stagger-i', index);
    });
  });

  /* ── 2. Tab Switching (Featured Collections) ── */
  const tabBtns = document.querySelectorAll('.tab-btn');
  tabBtns.forEach(btn => {
    btn.addEventListener('click', function () {
      const targetTab = this.getAttribute('data-tab') || this.dataset.tab;
      const container = this.closest('section');
      if (!container) return;

      // Scope active toggles to this section to prevent breaking other tabbed sections
      container.querySelectorAll('.tab-btn').forEach(b => {
        b.classList.remove('active');
        b.setAttribute('aria-selected', 'false');
      });
      container.querySelectorAll('.tab-content').forEach(c => {
        c.classList.remove('active');
      });

      this.classList.add('active');
      this.setAttribute('aria-selected', 'true');

      const targetEl = container.querySelector(`#tab-${targetTab}`);
      if (targetEl) {
        targetEl.classList.add('active');
        
        // Reset translate and scroll position of the carousel in the newly active tab
        const track = targetEl.querySelector('.carousel-track');
        if (track) {
          track.style.transform = 'translate3d(0px, 0, 0)';
          const wrapper = targetEl.querySelector('.tab-carousel');
          if (wrapper) wrapper.scrollLeft = 0;
        }

        // Only observe cards that haven't been revealed yet. Keep already revealed cards visible to make transitions instant.
        targetEl.querySelectorAll('.pcard').forEach((card, index) => {
          const observer = window.revealObserver || window.__revealObserver;
          if (observer && !card.classList.contains('revealed')) {
            card.style.setProperty('--stagger-i', index);
            observer.observe(card);
          }
        });
        
        // Dispatch resize to trigger layout check for slick/other sliders if any
        window.dispatchEvent(new Event('resize'));
      }
    });
  });

  /* ── 2b. Tab Carousel Custom Navigation ── */
  function initTabSliders() {
    document.querySelectorAll('.tab-carousel').forEach(wrapper => {
      const track = wrapper.querySelector('.carousel-track');
      if (!track) return;

      const container = wrapper.closest('section');
      if (!container) return;

      const panel = wrapper.closest('.tab-content');
      if (!panel) return;
      const tabId = panel.id;
      const navControls = container.querySelector(`[data-tab-nav-id="${tabId}"]`);
      if (!navControls) return;

      const prevBtn = navControls.querySelector('.prev');
      const nextBtn = navControls.querySelector('.next');
      if (!prevBtn || !nextBtn) return;

      let currentIndex = 0;

      function getVisibleCardsCount() {
        if (window.innerWidth <= 600) {
          const colsMobile = container.dataset.mobileCols;
          if (colsMobile === '1.2') return 1.2;
          if (colsMobile === '2.3') return 2.3;
          return parseFloat(colsMobile) || 2;
        } else if (window.innerWidth <= 900) {
          return 2.3; // tablet
        } else if (window.innerWidth <= 1200) {
          return 3;
        } else {
          return parseFloat(container.dataset.desktopCols) || 5;
        }
      }

      function getCardOffset() {
        const card = track.children[0];
        if (!card) return 0;
        const cardWidth = card.getBoundingClientRect().width;
        const gap = parseFloat(window.getComputedStyle(track).gap) || 0;
        return cardWidth + gap;
      }

      function updateNavButtons(maxIndex) {
        prevBtn.disabled = currentIndex <= 0;
        nextBtn.disabled = currentIndex >= maxIndex;
      }

      function slide(index) {
        const originalCards = Array.from(track.children);
        const totalCards = originalCards.length;
        if (totalCards === 0) return;
        
        const visibleCards = getVisibleCardsCount();
        const maxIndex = Math.max(0, totalCards - Math.floor(visibleCards));

        if (index < 0) index = 0;
        if (index > maxIndex) index = maxIndex;

        currentIndex = index;
        const step = getCardOffset();

        const isRTL = window.getComputedStyle(container).direction === 'rtl';
        const isMobileCarousel = window.innerWidth <= 600 && 
          (container.dataset.mobileCols === '1.2' || container.dataset.mobileCols === '2.3');

        if (isMobileCarousel) {
          const scrollTarget = currentIndex * step;
          wrapper.scrollTo({
            left: isRTL ? -scrollTarget : scrollTarget,
            behavior: 'smooth'
          });
        } else {
          const translateVal = isRTL ? (currentIndex * step) : -(currentIndex * step);
          track.style.transform = `translate3d(${translateVal}px, 0, 0)`;
        }
        
        updateNavButtons(maxIndex);
      }

      // Event Listeners for Nav Buttons
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        slide(currentIndex - 1);
      });

      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        slide(currentIndex + 1);
      });

      // Recalculate on resize or tab activations
      window.addEventListener('resize', () => {
        const originalCards = Array.from(track.children);
        const visibleCards = getVisibleCardsCount();
        const maxIndex = Math.max(0, originalCards.length - Math.floor(visibleCards));
        slide(currentIndex);
      });

      // Handle swipe and manual scroll tracker on mobile
      wrapper.addEventListener('scroll', () => {
        const isMobileCarousel = window.innerWidth <= 600 && 
          (container.dataset.mobileCols === '1.2' || container.dataset.mobileCols === '2.3');
        if (!isMobileCarousel) return;

        const step = getCardOffset();
        if (step > 0) {
          const isRTL = window.getComputedStyle(container).direction === 'rtl';
          const scrollLeftVal = isRTL ? Math.abs(wrapper.scrollLeft) : wrapper.scrollLeft;
          currentIndex = Math.round(scrollLeftVal / step);
          const originalCards = Array.from(track.children);
          const maxIndex = Math.max(0, originalCards.length - Math.floor(getVisibleCardsCount()));
          updateNavButtons(maxIndex);
        }
      }, { passive: true });

      // Drag support on desktop (using touch/pointer events)
      let startX = 0;
      let isDragging = false;
      let currentTranslate = 0;
      let prevTranslate = 0;

      wrapper.addEventListener('pointerdown', (e) => {
        const isMobileGrid = window.innerWidth <= 600 && 
          (container.dataset.mobileCols === '1' || container.dataset.mobileCols === '2');
        if (isMobileGrid) return;

        isDragging = true;
        startX = e.clientX;
        track.classList.add('no-transition');
        
        const isRTL = window.getComputedStyle(container).direction === 'rtl';
        const step = getCardOffset();
        prevTranslate = isRTL ? (currentIndex * step) : -(currentIndex * step);
      });

      window.addEventListener('pointermove', (e) => {
        if (!isDragging) return;
        const currentX = e.clientX;
        const diffX = currentX - startX;
        currentTranslate = prevTranslate + diffX;
        track.style.transform = `translate3d(${currentTranslate}px, 0, 0)`;
      });

      const handleDragEnd = (e) => {
        if (!isDragging) return;
        isDragging = false;
        track.classList.remove('no-transition');
        const diffX = e.clientX - startX;
        const threshold = 50; 

        const isRTL = window.getComputedStyle(container).direction === 'rtl';
        const directionFactor = isRTL ? -1 : 1;
        const adjustedDiff = diffX * directionFactor;

        if (adjustedDiff < -threshold) {
          slide(currentIndex + 1);
        } else if (adjustedDiff > threshold) {
          slide(currentIndex - 1);
        } else {
          slide(currentIndex);
        }
      };

      window.addEventListener('pointerup', handleDragEnd);
      window.addEventListener('pointercancel', handleDragEnd);

      // Listen for window resize to reset slide settings
      slide(0);
    });
  }
  initTabSliders();



  /* ── 3. Hero Carousel ── */
  const slides = document.querySelectorAll('.slide');
  const dots = document.querySelectorAll('.dot');
  let currentSlideIndex = 0;
  let slideInterval;

  function showSlide(index) {
    slides.forEach(s => s.classList.remove('active'));
    dots.forEach(d => d.classList.remove('active'));
    if (slides[index]) slides[index].classList.add('active');
    if (dots[index]) dots[index].classList.add('active');
    currentSlideIndex = index;
  }

  /* Legacy hero autoplay/dots disabled — the BOOT init (section 12) handles
     hero autoplay, dots, and touch swipe. Running both caused double-advance. */

  /* ── 4. Search Overlay ── */
  const searchOverlay = document.getElementById('search-overlay');
  const searchTriggerInput = document.getElementById('search-trigger-input');
  const closeSearch = document.getElementById('close-search');

  if (searchTriggerInput) {
    searchTriggerInput.addEventListener('focus', (e) => {
      e.preventDefault();
      searchTriggerInput.blur();
      if (searchOverlay) searchOverlay.classList.add('active');
      const inputField = document.getElementById('search-input-field');
      if (inputField) inputField.focus();
    });
  }

  if (closeSearch && searchOverlay) {
    closeSearch.addEventListener('click', () => {
      searchOverlay.classList.remove('active');
      searchOverlay.hidden = true;
      searchOverlay.setAttribute('hidden', '');
      document.body.classList.remove('overlay-open');
    });
  }

  /* ── 5. Cart Drawer ── */
  const cartDrawer = document.getElementById('cart-drawer');
  const openCart = document.getElementById('open-cart');
  const closeCart = document.getElementById('close-cart');

  /* Cart icon now navigates to the full cart page (it's an <a href="/cart">)
     instead of opening the side drawer — so we do NOT preventDefault here. */
  if (false && openCart && cartDrawer) {
    openCart.addEventListener('click', (e) => {
      e.preventDefault();
      cartDrawer.classList.add('active');
      renderCartDrawer();
    });
  }

  if (closeCart && cartDrawer) {
    closeCart.addEventListener('click', () => cartDrawer.classList.remove('active'));
  }

  /* Lock the page scroll behind drawers while they are open */
  const syncMenuBodyLock = () => {
    const drawer = document.getElementById('mobile-nav-drawer');
    const cart = document.getElementById('cart-drawer');
    const account = document.getElementById('account-drawer');
    const search = document.getElementById('search-overlay');
    const isAnyOpen = 
      (drawer && drawer.classList.contains('active')) ||
      (cart && cart.classList.contains('active')) ||
      (account && account.classList.contains('active')) ||
      (search && search.classList.contains('active'));
    document.body.classList.toggle('drawer-open', !!isAnyOpen);
  };

  /* ── 7. Account Drawer ── */
  const accountTriggers = document.querySelectorAll('.account-trigger');
  const accountDrawer = document.getElementById('account-drawer');
  const closeAccount = document.getElementById('close-account');

  if (accountTriggers.length > 0 && accountDrawer) {
    accountTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        accountDrawer.classList.add('active');
        if (cartDrawer) cartDrawer.classList.remove('active');
        if (mobileNavDrawer) mobileNavDrawer.classList.remove('active');
        syncMenuBodyLock();
      });
    });
  }
  if (closeAccount && accountDrawer) {
    closeAccount.addEventListener('click', () => accountDrawer.classList.remove('active'));
  }

  /* -- Close drawers on outside click -- */
  document.addEventListener('click', (e) => {
    if (cartDrawer && cartDrawer.classList.contains('active')) {
      if (!cartDrawer.contains(e.target) && !e.target.closest('#open-cart') && !e.target.closest('#mobile-bottom-cart-btn')) {
        cartDrawer.classList.remove('active');
      }
    }
    if (accountDrawer && accountDrawer.classList.contains('active')) {
      if (!accountDrawer.contains(e.target) && !e.target.closest('.account-trigger')) {
        accountDrawer.classList.remove('active');
      }
    }
  });

  /* ── 8. Mobile Nav Accordion ── */
  const accordionTriggers = document.querySelectorAll('.mobile-menu-link.has-accordion, .mobile-menu-link-level2.has-accordion');
  accordionTriggers.forEach(trigger => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      const parent = trigger.parentElement;
      const submenu = parent.querySelector('ul');
      const icon = trigger.querySelector('.accordion-icon');
      if (submenu) {
        const isOpen = submenu.style.maxHeight && submenu.style.maxHeight !== '0px';
        if (isOpen) {
          submenu.style.maxHeight = '0px';
          if (icon) icon.textContent = '+';

          let parentSubmenu = trigger.closest('.mobile-submenu');
          if (parentSubmenu) {
            parentSubmenu.style.maxHeight = Math.max(0, parentSubmenu.clientHeight - submenu.scrollHeight) + 'px';
          }
        } else {
          submenu.style.maxHeight = submenu.scrollHeight + 'px';
          if (icon) icon.textContent = '−';

          let parentSubmenu = trigger.closest('.mobile-submenu');
          if (parentSubmenu) {
            parentSubmenu.style.maxHeight = (parentSubmenu.clientHeight + submenu.scrollHeight) + 'px';
          }
        }
      }
    });
  });

  /* ── 9. Infinite Auto-Scrolling Carousel ── */
  const carouselWrappers = document.querySelectorAll('.carousel-wrapper:not(.tab-carousel):not(.cat-slider-wrapper)');
  carouselWrappers.forEach(wrapper => {
    const track = wrapper.querySelector('.carousel-track');
    if (!track) return;

    // Reset previous timers if theme.js runs multiple times or page is loaded from cache
    if (track.autoplayTimer) {
      clearInterval(track.autoplayTimer);
      track.autoplayTimer = null;
    }
    if (track.transitionFallbackTimer) {
      clearTimeout(track.transitionFallbackTimer);
      track.transitionFallbackTimer = null;
    }

    // Clean up previous clones (marked with .carousel-clone) before copying again
    track.querySelectorAll('.carousel-clone').forEach(clone => clone.remove());

    const isCatCircle = track.id === 'cat-circle-track';

    const originalCards = Array.from(track.children);
    const originalCount = originalCards.length;
    if (originalCount === 0) return;

    if (isCatCircle) {
      // Prepend 2 sets of clones in reverse order to keep correct sequence at beginning
      for (let s = 0; s < 2; s++) {
        for (let i = originalCount - 1; i >= 0; i--) {
          const clone = originalCards[i].cloneNode(true);
          clone.classList.add('carousel-clone');
          track.insertBefore(clone, track.firstChild);
        }
      }
      // Append 2 sets of clones to the end
      for (let s = 0; s < 2; s++) {
        originalCards.forEach(card => {
          const clone = card.cloneNode(true);
          clone.classList.add('carousel-clone');
          track.appendChild(clone);
        });
      }
    } else {
      // Append clones to end (marked so CSS can hide them in static/grid layouts)
      originalCards.forEach(card => {
        const clone = card.cloneNode(true);
        clone.classList.add('carousel-clone');
        track.appendChild(clone);
      });
    }

    // Carousel items must never be hidden by the scroll-reveal engine.
    // Clones are never observed and the horizontally-moving track breaks
    // in-view detection, which is what left the circles blank after a
    // back-navigation. Force every track child visible.
    Array.from(track.children).forEach(child => {
      child.classList.remove('reveal-on-scroll', 'm-reveal');
      child.classList.add('revealed');
    });

    let currentIndex = isCatCircle ? originalCount * 2 : 0;
    let isTransitioning = false;
    let isDragging = false;
    let startX = 0;
    let currentTranslate = 0;
    let prevTranslate = 0;
    const customSpeed = parseInt(wrapper.dataset.autoplaySpeed, 10);
    const slideIntervalDuration = customSpeed > 0 ? customSpeed : (isCatCircle ? 2000 : 4000);

    function getCardOffset() {
      const card = track.children[0];
      const cardWidth = card.getBoundingClientRect().width;
      const gap = parseFloat(window.getComputedStyle(track).gap) || 0;
      return cardWidth + gap;
    }

    function slide(targetIndex, transition = true) {
      if (transition) {
        isTransitioning = true;
        track.classList.remove('no-transition');
        // Safety net: if transitionend never fires (hidden tab etc.), unblock autoplay
        clearTimeout(track.transitionFallbackTimer);
        track.transitionFallbackTimer = setTimeout(() => { isTransitioning = false; }, 800);
      } else {
        // No transition = no transitionend event, so never mark as transitioning
        isTransitioning = false;
        track.classList.add('no-transition');
      }
      const step = getCardOffset();
      currentTranslate = -(targetIndex * step);
      track.style.transform = `translate3d(${currentTranslate}px, 0, 0)`;
      currentIndex = targetIndex;
      prevTranslate = currentTranslate;
    }

    function startAutoplay() {
      stopAutoplay();
      track.autoplayTimer = setInterval(() => {
        if (!isTransitioning) {
          slide(currentIndex + 1);
        }
      }, slideIntervalDuration);
    }

    function stopAutoplay() {
      if (track.autoplayTimer) {
        clearInterval(track.autoplayTimer);
        track.autoplayTimer = null;
      }
    }

    track.addEventListener('transitionend', (e) => {
      // Ignore bubbled transitions from child hover effects — only the track's own slide
      if (e.target !== track || e.propertyName !== 'transform') return;
      isTransitioning = false;
      if (isCatCircle) {
        if (currentIndex >= originalCount * 3) {
          slide(currentIndex - originalCount, false);
        } else if (currentIndex < originalCount * 2) {
          slide(currentIndex + originalCount, false);
        }
      } else {
        if (currentIndex >= originalCount) slide(0, false);
        else if (currentIndex < 0) slide(originalCount - 1, false);
      }
    });

    wrapper.addEventListener('mouseenter', stopAutoplay);
    wrapper.addEventListener('mouseleave', startAutoplay);

    wrapper.addEventListener('pointerdown', (e) => {
      isDragging = true;
      startX = e.clientX;
      stopAutoplay();
      track.classList.add('no-transition');
    });

    window.addEventListener('pointermove', (e) => {
      if (!isDragging) return;
      track.style.transform = `translate3d(${prevTranslate + (e.clientX - startX)}px, 0, 0)`;
    });

    const handleDragEnd = (e) => {
      if (!isDragging) return;
      isDragging = false;
      const diffX = e.clientX - startX;
      if (diffX < -50) slide(currentIndex + 1);
      else if (diffX > 50) slide(currentIndex - 1);
      else slide(currentIndex);
      startAutoplay();
    };

    window.addEventListener('pointerup', handleDragEnd);
    window.addEventListener('pointercancel', handleDragEnd);

    window.addEventListener('resize', () => {
      track.classList.add('no-transition');
      slide(currentIndex, false);
    });

    // Expose to the section's own prev/next buttons (e.g. related-products'
    // .related-nav-btn) so clicking them drives this same auto-play/drag
    // track instead of fighting it with a second, separate slide system.
    const navControls = wrapper.parentElement.querySelector('[data-related-nav]');
    if (navControls) {
      const prevBtn = navControls.querySelector('.prev');
      const nextBtn = navControls.querySelector('.next');
      if (prevBtn) prevBtn.addEventListener('click', () => { stopAutoplay(); slide(currentIndex - 1); startAutoplay(); });
      if (nextBtn) nextBtn.addEventListener('click', () => { stopAutoplay(); slide(currentIndex + 1); startAutoplay(); });
    }

    slide(currentIndex, false);
    startAutoplay();
  });

  /* ── 9b. Mobile auto-scroll for horizontal block rows ── */
  const autoScrollRows = document.querySelectorAll('.trust-grid, .floating-cards-grid, .reviews-track');
  autoScrollRows.forEach(row => {
    let rowTimer = null;
    let resumeTimer = null;

    function isScrollable() {
      return window.innerWidth <= 768 && row.scrollWidth > row.clientWidth + 10;
    }

    function stepScroll() {
      if (!isScrollable()) return;
      const card = row.firstElementChild;
      if (!card) return;
      const gap = parseFloat(window.getComputedStyle(row).gap) || 15;
      const stepSize = card.getBoundingClientRect().width + gap;
      const maxScroll = row.scrollWidth - row.clientWidth;
      if (row.scrollLeft >= maxScroll - 5) {
        row.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        row.scrollBy({ left: stepSize, behavior: 'smooth' });
      }
    }

    function startRowAutoScroll() {
      stopRowAutoScroll();
      if (!isScrollable()) return;
      rowTimer = setInterval(stepScroll, 3500);
    }

    function stopRowAutoScroll() {
      if (rowTimer) { clearInterval(rowTimer); rowTimer = null; }
    }

    function pauseThenResume() {
      stopRowAutoScroll();
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(startRowAutoScroll, 5000);
    }

    ['touchstart', 'pointerdown', 'wheel'].forEach(evt =>
      row.addEventListener(evt, pauseThenResume, { passive: true })
    );
    window.addEventListener('resize', startRowAutoScroll);
    startRowAutoScroll();
  });

  /* ── 9c. Mega menu products strip — prev/next buttons ── */
  document.querySelectorAll('.mega-products-row-wrap').forEach(wrap => {
    const row = wrap.querySelector('.mega-products-row');
    if (!row) return;
    const step = () => Math.max(row.clientWidth * 0.8, 160);
    const prevBtn = wrap.querySelector('.mega-products-nav.prev');
    const nextBtn = wrap.querySelector('.mega-products-nav.next');
    if (prevBtn) prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      row.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    if (nextBtn) nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      row.scrollBy({ left: step(), behavior: 'smooth' });
    });
  });

  /* ── 9d. Mobile menu featured products ──
     Manual swipe slider (scroll-snap, one product per view) — no auto-rotate.
     Layout handled entirely in CSS. */

  /* ── 10. Add to Cart — Product Cards (Shopify) ── */
  // Moved to isolated global click handler at the end of the file for reliability

  /* ── 11. Product Page ── */
  initProductPage();

  /* ── 12. Product Form (Shopify variant form) ── */
  initProductForm();

  /* ── 13. Collections Page ── */
  initCollectionsPage();

  /* ── 14. Wishlist Page ── */
  initWishlistPage();

  /* ── 15. Premium Video Sections ── */
  initPremiumVideoSections();

  /* ── 16. Keyboard shortcuts ── */
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (searchOverlay) searchOverlay.classList.remove('active');
      if (cartDrawer) cartDrawer.classList.remove('active');
      if (mobileNavDrawer) mobileNavDrawer.classList.remove('active');
      if (accountDrawer) accountDrawer.classList.remove('active');
      syncMenuBodyLock();
    }
  });

  /* ── 17. Wishlist count badge ── */
  Wishlist.updateButtons();
  RecentlyViewed.renderInMenu();

  /* ── 18. Cart count on load ── */
  updateCartCount();

  /* ── 19. Button ripple ── */
  document.addEventListener('click', e => {
    const btn = e.target.closest('.btn, .btn--gold, .btn--ghost');
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const ripple = document.createElement('span');
    ripple.className = 'ripple-effect';
    const size = Math.max(rect.width, rect.height) * 0.5;
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${e.clientX - rect.left - size / 2}px`;
    ripple.style.top = `${e.clientY - rect.top - size / 2}px`;
    btn.appendChild(ripple);
    setTimeout(() => { if (ripple.parentNode) ripple.remove(); }, 600);
  }, { passive: true });

  /* ── 20. Slider Progress Bars ── */
  initSliderProgress();

  /* ── 21. Preloader ── */
  /* Called once from BOOT (below) — calling it here too caused two
     concurrent initPreloader() runs to race each other and made the
     preloader hang far longer than its own 1.5s max-show timer. */
});

/* ═══════════════════════════════════════
   PRODUCT PAGE FUNCTIONS
   ═══════════════════════════════════════ */
function initProductPage() {
  /* -- Gallery thumbnails + swipe: handled ENTIRELY by the product section's
        own script now. These old handlers double-fired alongside it, making
        "next" jump two images at once. Disabled. -- */

  /* -- Quantity adjuster -- */
  const qtyMinus = document.getElementById('qtyMinus');
  const qtyPlus = document.getElementById('qtyPlus');
  const qtyVal = document.getElementById('qtyInput');
  if (qtyMinus && qtyPlus && qtyVal) {
    qtyMinus.addEventListener('click', () => {
      const v = parseInt(qtyVal.value);
      if (v > 1) qtyVal.value = v - 1;
    });
    qtyPlus.addEventListener('click', () => {
      const v = parseInt(qtyVal.value);
      if (v < 10) qtyVal.value = v + 1;
    });
  }

  /* -- Accordion -- */
  const accItems = document.querySelectorAll('.acc-item');
  accItems.forEach(item => {
    const trigger = item.querySelector('.acc-trigger');
    const content = item.querySelector('.acc-content');
    if (trigger && content) {
      trigger.addEventListener('click', () => {
        const isActive = item.classList.contains('active');
        accItems.forEach(i => {
          i.classList.remove('active');
          const c = i.querySelector('.acc-content');
          if (c) c.style.maxHeight = null;
        });
        if (!isActive) {
          item.classList.add('active');
          content.style.maxHeight = content.scrollHeight + 'px';
        }
      });
    }
  });

  /* -- Size chart modal -- */
  const openSizeChart = document.getElementById('open-size-chart-modal');
  const sizeChartModal = document.getElementById('size-chart-modal');
  const closeSizeChart = document.getElementById('close-size-chart');
  if (openSizeChart && sizeChartModal) {
    openSizeChart.addEventListener('click', (e) => { e.preventDefault(); sizeChartModal.classList.add('active'); });
  }
  const hideSizeGuide = () => { if (sizeChartModal) sizeChartModal.classList.remove('active'); };
  if (closeSizeChart) closeSizeChart.addEventListener('click', hideSizeGuide);

  /* -- Sticky mobile bar -- */
  const stickyBar = document.querySelector('.sticky-mobile-bar');
  const purchaseRow = document.querySelector('.purchase-row');
  if (stickyBar && purchaseRow) {
    const stickObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        stickyBar.classList.toggle('visible', !entry.isIntersecting);
      });
    }, { threshold: 0.1 });
    stickObserver.observe(purchaseRow);
  }

  /* -- Track recently viewed -- */
  const productContainer = document.querySelector('[data-product-id]');
  if (productContainer) {
    const productId = productContainer.dataset.productId;
    const productData = {
      title: productContainer.dataset.productTitle || document.title,
      price: productContainer.dataset.productPrice || '',
      image: productContainer.dataset.productImage || '',
      url: window.location.href
    };
    RecentlyViewed.track(productId, productData);
  }

  /* -- Reviews slider -- */
  const reviewsList = document.querySelectorAll('.review-slide-item');
  if (reviewsList.length > 0) {
    let currentReviewIdx = 0;
    function showReview(index) {
      reviewsList.forEach((item, i) => {
        item.style.display = i === index ? 'flex' : 'none';
      });
    }
    showReview(0);
    setInterval(() => {
      currentReviewIdx = (currentReviewIdx + 1) % reviewsList.length;
      showReview(currentReviewIdx);
    }, 6000);
  }

  /* -- Delivery dates -- */
  const ordD = document.getElementById('ordered-date');
  if (ordD) {
    const today = new Date();
    const fmt = (d) => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'Asia/Karachi' }).format(d);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const delStart = new Date(today); delStart.setDate(delStart.getDate() + 3);
    const delEnd = new Date(today); delEnd.setDate(delEnd.getDate() + 4);
    if (ordD) ordD.textContent = fmt(today);
    const dispD = document.getElementById('dispatched-date');
    if (dispD) dispD.textContent = `${fmt(today)} - ${fmt(tomorrow)}`;
    const delD = document.getElementById('delivered-date');
    if (delD) delD.textContent = `${fmt(delStart)} - ${fmt(delEnd)}`;
  }
}

/* ═══════════════════════════════════════
   SHOPIFY PRODUCT FORM
   ═══════════════════════════════════════ */
function initProductForm() {
  initProductPageVariants();
}

function initProductPageVariants() {
  const form = document.querySelector('.product-form');
  if (!form) return;

  const addToCartBtn = form.querySelector('[name="add"]');
  const variantInput = form.querySelector('[name="id"]');
  const variantsScript = document.getElementById('product-variants-json');
  if (!variantsScript || !variantInput) return;

  let variants = [];
  try {
    variants = JSON.parse(variantsScript.textContent);
  } catch (e) {
    console.error('Failed to parse product variants JSON:', e);
    return;
  }

  const variantBtns = form.querySelectorAll('.variant-btn');
  variantBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const groupEl = btn.closest('.variant-group');
      if (groupEl) {
        groupEl.querySelectorAll('.variant-btn').forEach(b => {
          b.classList.remove('active');
          b.setAttribute('aria-pressed', 'false');
        });
      }
      
      btn.classList.add('active');
      btn.setAttribute('aria-pressed', 'true');

      const labelVal = groupEl ? groupEl.querySelector('.selected-value') : null;
      if (labelVal) {
        labelVal.textContent = btn.dataset.value || btn.textContent;
      }

      // Variant matching logic
      const activeBtns = Array.from(form.querySelectorAll('.variant-group')).map(group => group.querySelector('.variant-btn.active'));
      const selectedValues = activeBtns.map(b => b ? b.dataset.value : null);

      const matchedVariant = variants.find(variant => {
        return variant.options.every((opt, index) => opt === selectedValues[index]);
      });

      if (matchedVariant) {
        variantInput.value = matchedVariant.id;

        const priceCurrent = document.querySelector('.price-current');
        const priceOriginal = document.querySelector('.price-original');
        const discountPill = document.querySelector('.discount-pill');

        if (priceCurrent) {
          priceCurrent.textContent = ShopifyCart.formatMoney(matchedVariant.price);
        }

        if (matchedVariant.compare_at_price > matchedVariant.price) {
          if (priceOriginal) {
            priceOriginal.textContent = ShopifyCart.formatMoney(matchedVariant.compare_at_price);
            priceOriginal.style.display = '';
          }
          if (discountPill) {
            const discount = Math.round((matchedVariant.compare_at_price - matchedVariant.price) * 100 / matchedVariant.compare_at_price);
            discountPill.textContent = `${discount}% OFF`;
            discountPill.style.display = '';
          }
        } else {
          if (priceOriginal) priceOriginal.style.display = 'none';
          if (discountPill) discountPill.style.display = 'none';
        }

        if (matchedVariant.available) {
          if (addToCartBtn) {
            addToCartBtn.disabled = false;
            addToCartBtn.textContent = 'Add to Bag';
            addToCartBtn.classList.remove('disabled');
          }
        } else {
          if (addToCartBtn) {
            addToCartBtn.disabled = true;
            addToCartBtn.textContent = 'Sold Out';
            addToCartBtn.classList.add('disabled');
          }
        }

        const codBtn = document.getElementById('cod-order-btn');
        if (codBtn) {
          codBtn.dataset.variantId = matchedVariant.id;
          codBtn.dataset.variantTitle = matchedVariant.title;
        }

        const stickyPrice = document.querySelector('.sticky-bar-price');
        if (stickyPrice) {
          stickyPrice.textContent = ShopifyCart.formatMoney(matchedVariant.price);
        }

        if (matchedVariant.featured_image) {
          const featuredSrc = matchedVariant.featured_image.src;
          const getBaseName = (url) => {
            if (!url) return '';
            const parts = url.split('/');
            const file = parts[parts.length - 1].split('?')[0];
            return file.replace(/(_\d+x\d*|_large|_medium|_small|_thumb)/g, '');
          };
          const targetBase = getBaseName(featuredSrc);
          const thumbs = document.querySelectorAll('.gallery-thumbs .thumb');
          let matchedThumb = null;
          for (let thumb of thumbs) {
            const thumbImg = thumb.querySelector('img');
            if (thumbImg && getBaseName(thumbImg.src) === targetBase) {
              matchedThumb = thumb;
              break;
            }
          }
          if (matchedThumb) {
            matchedThumb.click();
          }
        }
      } else {
        if (addToCartBtn) {
          addToCartBtn.disabled = true;
          addToCartBtn.textContent = 'Unavailable';
          addToCartBtn.classList.add('disabled');
        }
      }
    });
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const variantId = variantInput ? variantInput.value : null;
    if (!variantId) return;

    const qtyInput = form.querySelector('[name="quantity"]');
    const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;

    const submitBtn = form.querySelector('[type="submit"]');
    await addToCartWithFeedback(variantId, qty, submitBtn);
  });
}

/* ═══════════════════════════════════════
   COLLECTIONS PAGE
   ═══════════════════════════════════════ */
function initCollectionsPage() {
  const catalogGrid = document.getElementById('catalog-grid');
  if (!catalogGrid) return;

  const viewSelectors = document.querySelectorAll('.view-selector');
  const filterToggle = document.getElementById('filter-toggle-btn');
  const layoutContainer = document.querySelector('.collections-layout');
  const filterSidebar = document.querySelector('.filter-sidebar');

  /* -- View selector -- */
  const savedView = localStorage.getItem('bi-collections-view') || '4';
  applyGridLayout(savedView);

  viewSelectors.forEach(selector => {
    selector.querySelectorAll('.view-btn').forEach(btn => {
      btn.addEventListener('click', () => applyGridLayout(btn.getAttribute('data-view')));
    });
  });

  function applyGridLayout(view) {
    viewSelectors.forEach(sel => {
      sel.querySelectorAll('.view-btn').forEach(btn => {
        btn.classList.toggle('active', btn.getAttribute('data-view') === view);
      });
    });

    catalogGrid.classList.add('grid-transitioning');
    setTimeout(() => {
      catalogGrid.classList.remove('grid-view-1', 'grid-view-2', 'grid-view-3', 'grid-view-4');
      catalogGrid.classList.add(`grid-view-${view}`);
      localStorage.setItem('bi-collections-view', view);
      setTimeout(() => {
        catalogGrid.classList.remove('grid-transitioning');
        catalogGrid.querySelectorAll('.pcard').forEach((card, index) => {
          card.classList.remove('revealed');
          card.classList.add('m-reveal');
          card.style.setProperty('--stagger-i', index % 6);
          if (window.revealObserver) window.revealObserver.observe(card);
        });
      }, 50);
    }, 200);
  }

  /* -- Filter sidebar toggle -- */
  if (filterToggle) {
    filterToggle.addEventListener('click', (e) => {
      e.stopPropagation();
      if (window.innerWidth > 768) {
        if (layoutContainer) layoutContainer.classList.toggle('sidebar-collapsed');
      } else {
        if (filterSidebar) filterSidebar.classList.toggle('active');
      }
    });
  }

  const closeFilters = document.getElementById('close-filters');
  if (closeFilters && filterSidebar) {
    closeFilters.addEventListener('click', () => filterSidebar.classList.remove('active'));
  }

  document.addEventListener('click', (e) => {
    if (window.innerWidth <= 768 && filterSidebar && filterSidebar.classList.contains('active')) {
      if (!filterSidebar.contains(e.target) && filterToggle && !filterToggle.contains(e.target)) {
        filterSidebar.classList.remove('active');
      }
    }
  });

  /* -- Scroll reveal for cards -- */
  catalogGrid.querySelectorAll('.pcard').forEach((card, index) => {
    card.classList.add('m-reveal');
    card.style.setProperty('--stagger-i', index % 6);
    if (window.revealObserver) window.revealObserver.observe(card);
  });
}

/* ═══════════════════════════════════════
   WISHLIST PAGE
   ═══════════════════════════════════════ */
function initWishlistPage() {
  const wishlistGrid = document.getElementById('wishlist-grid');
  const emptyState = document.getElementById('wishlist-empty-state');
  if (!wishlistGrid) return;

  function renderWishlist() {
    const items = Wishlist.getAll();
    if (items.length === 0) {
      wishlistGrid.innerHTML = '';
      if (emptyState) emptyState.style.display = 'block';
      return;
    }
    if (emptyState) emptyState.style.display = 'none';

    wishlistGrid.innerHTML = items.map((item, i) => {
      const badges = ['Only 2 left in stock!', 'Selling Fast!', 'High Demand', 'Almost Gone!'];
      const randomBadge = badges[Math.floor(Math.random() * badges.length)];
      
      return `
      <article class="pcard m-reveal revealed" style="--stagger-i:${i % 6}">
        <div class="pcard__media">
          <div class="pcard-badge-container">
            <span class="pcard-badge custom-badge" style="background: var(--gold); color: #000; font-weight: bold;">
              ${randomBadge}
            </span>
          </div>
          <button class="pcard-wishlist-btn" data-wishlist-id="${item.id}" data-wishlist-title="${item.title}" data-wishlist-price="${item.price}" data-wishlist-image="${item.image}" data-wishlist-url="${item.url}" title="Remove from Wishlist">
            <svg viewBox="0 0 24 24" style="fill:var(--gold);stroke:var(--gold);"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
          </button>
          <a href="${item.url}" class="pcard__link" aria-label="${item.title}" style="display:block;position:absolute;inset:0;z-index:5;">
            <img loading="lazy" src="${item.image}" alt="${item.title}" class="primary-img">
          </a>
        </div>
        <div class="pcard__body">
          <h3 class="pcard__title">${item.title}</h3>
          <div class="price-row">
            <span class="pcard__price">${item.price}</span>
          </div>
          <div class="wishlist-scarcity-block" style="margin-bottom: 12px; margin-top: -2px;">
            <div style="font-size: 0.75rem; color: #ff4d4d; font-weight: 600; display: flex; align-items: center; gap: 4px; line-height: 1.2; margin-bottom: 4px;">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><circle cx="6" cy="19" r="2"/><circle cx="17" cy="19" r="2"/><path d="M2 2h2l3.6 7.6L5.2 14c-.1.3-.2.6-.2 1 0 1.1.9 2 2 2h12v-2H7.4c-.1 0-.2-.1-.2-.2v-.1l.9-1.7h7.4c.7 0 1.4-.4 1.7-1l3.9-7c.1-.2.1-.4.1-.6 0-.5-.4-1-1-1H5.2L4.3 2H2v2zm14 15c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/></svg>
              ${Math.floor(Math.random() * 8) + 2} people have this in their cart!
            </div>
            <div style="height: 4px; width: 100%; background: rgba(255, 77, 77, 0.2); border-radius: 2px; overflow: hidden; position: relative;">
              <div style="height: 100%; width: ${Math.floor(Math.random() * 15) + 5}%; background: #ff4d4d; border-radius: 2px; position: absolute; left: 0; top: 0;"></div>
            </div>
            <div style="font-size: 0.65rem; color: #ff4d4d; text-align: right; margin-top: 2px; font-weight: bold;">Only a few left</div>
          </div>
          <div class="pcard-actions-row">
            <a href="${item.url}" class="btn btn--gold pcard-add-btn buy-now-animated">
              <svg class="btn-cart-icon" viewBox="0 0 24 24"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg>
              View Product
            </a>
          </div>
        </div>
      </article>
      `;
    }).join('');

    wishlistGrid.querySelectorAll('.pcard').forEach(card => {
      if (window.revealObserver) window.revealObserver.observe(card);
    });
  }

  renderWishlist();

  // Re-render when wishlist changes
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-wishlist-id]')) {
      setTimeout(renderWishlist, 100);
    }
  });
}

/* ═══════════════════════════════════════
   PREMIUM VIDEO SECTIONS ENGINE
   ═══════════════════════════════════════ */
function initPremiumVideoSections() {
  // 1. Initialize any 3D Coverflow Carousels
  document.querySelectorAll('[id^="wheel-carousel-"]').forEach(carouselSection => {
    const sectionId = carouselSection.id.replace('wheel-carousel-', '');
    const wheelTrack = document.getElementById(`wheel-track-${sectionId}`);
    const prevBtn = document.getElementById(`wheel-prev-${sectionId}`);
    const nextBtn = document.getElementById(`wheel-next-${sectionId}`);
    if (!wheelTrack) return;

    // Use cards rendered directly by Liquid
    const cards = wheelTrack.querySelectorAll('.wheel-card');
    const total = cards.length;
    if (total === 0) return;

    const prevBtnMob = document.getElementById(`wheel-prev-mob-${sectionId}`);
    const nextBtnMob = document.getElementById(`wheel-next-mob-${sectionId}`);
    let coverflowIndex = 0;

    function updateCoverflow() {
      cards.forEach((card, idx) => {
        let offset = idx - coverflowIndex;
        if (offset < -Math.floor(total / 2)) offset += total;
        if (offset > Math.floor(total / 2)) offset -= total;

        const absOffset = Math.abs(offset);
        const rotateY = -offset * 35;
        const translateZ = 120 - (absOffset * 100);
        const isMobile = window.innerWidth < 768;
        const cardWidth = card.offsetWidth || (isMobile ? 160 : 220);
        const overlapFactor = isMobile ? 0.65 : 0.72; // Spreads blocks out beautifully without overlapping left column text
        const translateX = offset * (cardWidth * overlapFactor);
        const scale = 1 - (absOffset * 0.16);
        const opacity = absOffset > 1 ? 0 : (absOffset === 0 ? 1 : 0.65);
        const brightness = absOffset === 0 ? 1 : 0.45;

        card.style.transform = `translate3d(${translateX}px, 0, ${translateZ}px) scale(${scale}) rotateY(${rotateY}deg)`;
        card.style.opacity = opacity;
        card.style.zIndex = 10 - absOffset;
        card.style.filter = `brightness(${brightness})`;
        card.setAttribute('aria-hidden', absOffset === 0 ? 'false' : 'true');

        const video = card.querySelector('video');
        if (video) {
          const wrap = video.closest('.wheel-card-video-wrap');
          if (offset === 0) {
            card.classList.add('active');
            // Only slides that were sitting at preload="none" need a fresh
            // load kicked off here — the first slide already preloaded on
            // page load, so re-triggering .load() on it would restart it.
            if (video.preload !== 'auto') {
              if (wrap) wrap.classList.add('is-loading');
              video.addEventListener('canplay', function onReady() {
                if (wrap) wrap.classList.remove('is-loading');
                video.removeEventListener('canplay', onReady);
              });
              video.preload = 'auto';
              video.load();
            }
            video.play().catch(() => { });

            // Sync active text pane on left side
            const textBlocks = carouselSection.querySelectorAll('.coverflow-text-block');
            textBlocks.forEach(block => {
              const blockIdx = parseInt(block.getAttribute('data-text-idx'), 10);
              if (blockIdx === idx) {
                block.classList.add('active');
              } else {
                block.classList.remove('active');
              }
            });
          } else {
            card.classList.remove('active');
            video.pause();
          }
        }
      });
    }

    function nextCoverflow() { coverflowIndex = (coverflowIndex + 1) % total; updateCoverflow(); }
    function prevCoverflow() { coverflowIndex = (coverflowIndex - 1 + total) % total; updateCoverflow(); }

    if (prevBtn) prevBtn.addEventListener('click', e => { e.preventDefault(); prevCoverflow(); });
    if (nextBtn) nextBtn.addEventListener('click', e => { e.preventDefault(); nextCoverflow(); });
    if (prevBtnMob) prevBtnMob.addEventListener('click', e => { e.preventDefault(); prevCoverflow(); });
    if (nextBtnMob) nextBtnMob.addEventListener('click', e => { e.preventDefault(); nextCoverflow(); });

    let tsX = 0;
    wheelTrack.addEventListener('touchstart', e => { tsX = e.touches[0].clientX; }, { passive: true });
    wheelTrack.addEventListener('touchend', e => {
      const diff = tsX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) { if (diff > 0) nextCoverflow(); else prevCoverflow(); }
    }, { passive: true });

    updateCoverflow();
  });

  // 2. Initialize any Floating Product Video Cards
  document.querySelectorAll('[id^="floating-cards-container-"]').forEach(floatSection => {
    const floatCards = floatSection.querySelectorAll('.floating-video-card');
    if (floatCards.length === 0) return;

    floatCards.forEach(card => {
      // Play on mouseenter, pause on mouseleave
      const video = card.querySelector('video');
      if (video) {
        card.addEventListener('mouseenter', () => {
          video.setAttribute('preload', 'auto'); // Preload dynamically on hover
          video.play().catch(() => { });
        });
        card.addEventListener('mouseleave', () => {
          video.pause();
          video.currentTime = 0;
        });
        card.addEventListener('touchstart', () => {
          video.setAttribute('preload', 'auto');
          if (video.paused) {
            floatSection.querySelectorAll('video').forEach(v => v.pause());
            video.play().catch(() => { });
          } else {
            video.pause();
          }
        }, { passive: true });
      }

      // Parallax effect
      card.addEventListener('mousemove', e => {
        if (window.innerWidth < 768) return;
        const r = card.getBoundingClientRect();
        const rotateY = ((e.clientX - r.left - r.width / 2) / (r.width / 2)) * 7;
        const rotateX = -((e.clientY - r.top - r.height / 2) / (r.height / 2)) * 7;
        card.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-5px)`;
      });
      card.addEventListener('mouseleave', () => { card.style.transform = ''; });
    });
  });

  // 3. Auto-play/pause videos on visibility (General observer)
  const videoObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const video = entry.target;
      if (entry.isIntersecting) {
        const card = video.closest('.wheel-card');
        if (card && !card.classList.contains('active')) return;
        video.setAttribute('preload', 'auto');
        video.play().catch(() => { });
      } else {
        video.pause();
      }
    });
  }, { threshold: 0.1 });

  document.querySelectorAll('.wheel-card video, .floating-video-card video').forEach(video => videoObserver.observe(video));
}

/* ═══════════════════════════════════════
   SLIDER PROGRESS BARS
   ═══════════════════════════════════════ */
function initSliderProgress() {
  document.querySelectorAll('.carousel-wrapper').forEach(wrapper => {
    const track = wrapper.querySelector('.carousel-track');
    if (!track) return;

    let progressContainer = wrapper.nextElementSibling;
    if (!progressContainer || !progressContainer.classList.contains('slider-progress-container')) {
      progressContainer = document.createElement('div');
      progressContainer.className = 'slider-progress-container';
      const bar = document.createElement('div');
      bar.className = 'slider-progress-bar';
      progressContainer.appendChild(bar);
      wrapper.parentNode.insertBefore(progressContainer, wrapper.nextSibling);
    }

    const progressBar = progressContainer.querySelector('.slider-progress-bar');
    if (!progressBar) return;

    const updateProgress = () => {
      const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
      if (maxScroll <= 0) { progressBar.style.width = '0%'; return; }
      progressBar.style.width = `${Math.min(100, (wrapper.scrollLeft / maxScroll) * 100)}%`;
    };

    wrapper.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);
    setTimeout(updateProgress, 300);
  });
}

/* ═══════════════════════════════════════
   PRELOADER
   ═══════════════════════════════════════ */
function initPreloader() {
  const preloader = document.getElementById('preloader');
  if (!preloader) return;

  document.body.classList.add('is-preloading');

  const MIN_SHOW_MS = 300;
  const MAX_SHOW_MS = 1500;
  const startTime = performance.now();

  function hidePreloader() {
    const delay = Math.max(0, MIN_SHOW_MS - (performance.now() - startTime));
    setTimeout(() => {
      if (!preloader || !preloader.parentNode) {
        // Preloader already gone — still make sure the scroll-lock class is
        // lifted (a leftover is-preloading kept body overflow:hidden, which
        // silently broke the mobile sticky header).
        document.body.classList.remove('is-preloading');
        return;
      }
      preloader.classList.add('preloader-hidden');
      setTimeout(() => {
        if (preloader.parentNode) preloader.remove();
        document.body.classList.remove('is-preloading');
      }, 850);
    }, delay);
  }

  window.addEventListener('load', hidePreloader, { once: true });
  setTimeout(hidePreloader, MAX_SHOW_MS);
  // Hard safety: whatever happens, the scroll lock must be gone by 4s.
  setTimeout(() => document.body.classList.remove('is-preloading'), 4000);
}

/* ═══════════════════════════════════════════════════════════════════
   MASTER INITIALIZATION — reads window.THEME_CONFIG from theme.liquid
   ═══════════════════════════════════════════════════════════════════ */
const TC = window.THEME_CONFIG || {};

/* ── Cart count initializer ── */
async function syncCartCount() {
  try {
    const cart = await fetch('/cart.js').then(r => r.json());
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count || '';
    });
    const drawerTotal = document.getElementById('cart-drawer-total');
    if (drawerTotal && cart.total_price !== undefined) {
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency', currency: TC.currency || 'USD'
      }).format(cart.total_price / 100);
      drawerTotal.textContent = formatted;
    }
  } catch (_) { }
}

/* ── Shipping date calculator ── */
function initShippingDates() {
  const orderedEl = document.getElementById('ordered-date');
  const dispatchedEl = document.getElementById('dispatched-date');
  const deliveredEl = document.getElementById('delivered-date');
  if (!orderedEl) return;

  const dispatchDays = TC.dispatchDays || 2;
  const delivMin = TC.deliveryMin || 3;
  const delivMax = TC.deliveryMax || 5;

  const addBizDays = (date, days) => {
    let d = new Date(date), added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) added++;
    }
    return d;
  };

  const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const now = new Date();
  const dispatched = addBizDays(now, dispatchDays);
  const delivMin_ = addBizDays(dispatched, delivMin);
  const delivMax_ = addBizDays(dispatched, delivMax);

  orderedEl.textContent = 'Today';
  dispatchedEl.textContent = fmt(dispatched);
  deliveredEl.textContent = `${fmt(delivMin_)} – ${fmt(delivMax_)}`;
}

/* ── Accordion helper ── */
function initAccordions() {
  document.querySelectorAll('.acc-trigger, .mobile-menu-link.has-accordion').forEach(btn => {
    btn.addEventListener('click', function () {
      const expanded = this.getAttribute('aria-expanded') === 'true';
      const content = this.nextElementSibling;
      if (!content) return;
      this.setAttribute('aria-expanded', !expanded);
      const icon = this.querySelector('.accordion-icon');
      if (icon) icon.textContent = expanded ? '+' : '−';
      if (!expanded) {
        content.style.maxHeight = content.scrollHeight + 'px';
      } else {
        content.style.maxHeight = '0';
      }
    });
  });
}

/* ── Gallery (product page) ── */
function initProductGallery() {
  const galleryMain = document.getElementById('gallery-main');
  const mainImg = document.getElementById('mainImg');
  const thumbs = document.querySelectorAll('.gallery-thumbs .thumb');
  const imgCountEl = document.getElementById('img-count-current');
  if (!galleryMain || !mainImg || !thumbs.length) return;

  thumbs.forEach((thumb, i) => {
    thumb.addEventListener('click', () => {
      const src = thumb.dataset.src;
      if (src) {
        mainImg.style.opacity = '0';
        mainImg.style.transform = 'scale(0.97)';
        setTimeout(() => {
          mainImg.src = src;
          mainImg.style.opacity = '1';
          mainImg.style.transform = 'scale(1)';
        }, 200);
      }
      thumbs.forEach(t => { t.classList.remove('active'); t.setAttribute('aria-pressed', 'false'); });
      thumb.classList.add('active');
      thumb.setAttribute('aria-pressed', 'true');
      if (imgCountEl) imgCountEl.textContent = i + 1;
    });
  });

  mainImg.style.transition = 'opacity 0.3s ease, transform 0.4s cubic-bezier(0.25,1,0.5,1)';
}

/* ── Size chart modal ── */
function initSizeChartModal() {
  const openBtn = document.getElementById('open-size-chart-modal');
  const modal = document.getElementById('size-chart-modal');
  const closeBtn = document.getElementById('close-size-chart');
  if (!openBtn || !modal) return;

  openBtn.addEventListener('click', e => {
    e.preventDefault();
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  });

  const closeModal = () => {
    modal.style.display = 'none';
    document.body.style.overflow = '';
  };

  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  modal.addEventListener('click', e => { if (e.target === modal) closeModal(); });
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeModal(); });
}

/* ── Sticky mobile bar ── */
function initStickyMobileBar() {
  const bar = document.querySelector('.sticky-mobile-bar');
  if (!bar) return;

  const purchaseRow = document.querySelector('.purchase-row');
  if (!purchaseRow) { bar.classList.add('visible'); return; }

  const observer = new IntersectionObserver(([entry]) => {
    bar.classList.toggle('visible', !entry.isIntersecting);
  }, { threshold: 0 });

  observer.observe(purchaseRow);
}

/* ── Quantity adjuster (product page) ── */
function initQtyAdjuster() {
  const input = document.getElementById('qtyInput');
  const minus = document.getElementById('qtyMinus');
  const plus = document.getElementById('qtyPlus');
  if (!input || !minus || !plus) return;

  minus.addEventListener('click', () => {
    const val = parseInt(input.value, 10);
    if (val > 1) input.value = val - 1;
  });
  plus.addEventListener('click', () => {
    const val = parseInt(input.value, 10);
    const max = parseInt(input.max, 10) || 99;
    if (val < max) input.value = val + 1;
  });
}

/* ── Variant selection (product page) ── */
function initVariantSelectors() {
  // Handled inside initProductPageVariants()
}

/* ── Wishlist detail button (product page) ──
   The actual toggle is handled by the single global click handler (which
   calls Wishlist.toggle + shows the toast). Here we ONLY set the initial
   saved-state on load and keep aria-label in sync, so there is no second
   listener double-toggling the same click. */
function initWishlistDetailBtn() {
  const btn = document.getElementById('wishlistDetailBtn');
  if (!btn) return;
  const pid = btn.dataset.wishlistId;
  const syncLabel = () => {
    const on = btn.classList.contains('active');
    btn.setAttribute('aria-label', on ? 'Remove from Wishlist' : 'Save to Wishlist');
    btn.setAttribute('title', on ? 'Saved to Wishlist' : 'Save to Wishlist');
  };
  // Initial state from storage (Wishlist.updateButtons already toggles .active,
  // but call has() directly in case it hasn't run yet).
  if (typeof Wishlist !== 'undefined' && Wishlist.has(pid)) {
    btn.classList.add('active');
  }
  syncLabel();
  // Keep the label in sync after the global handler flips the class.
  btn.addEventListener('click', () => setTimeout(syncLabel, 0));
}

/* ── Search overlay keyboard trap ── */
function enhanceSearchOverlay() {
  const overlay = document.getElementById('search-overlay');
  const input = document.getElementById('search-input-field');
  const searchTriggerInput = document.getElementById('search-trigger-input');

  if (searchTriggerInput && overlay) {
    searchTriggerInput.addEventListener('focus', () => {
      overlay.hidden = false;
      overlay.removeAttribute('hidden');
      document.body.classList.add('overlay-open');
      setTimeout(() => { if (input) input.focus(); }, 50);
    });
    searchTriggerInput.addEventListener('click', () => {
      overlay.hidden = false;
      overlay.removeAttribute('hidden');
      document.body.classList.add('overlay-open');
      setTimeout(() => { if (input) input.focus(); }, 50);
    });
  }

  if (overlay) {
    overlay.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        overlay.hidden = true;
        document.body.classList.remove('overlay-open');
      }
    });
  }
}

/* ── Category Circles: Handled directly in sections/category-circles.liquid ── */
function initCategorySlider() {
  return;
}

/* ── Custom Reviews Widget (rotating stack of 10 items) ── */
function initReviewsWidget() {
  const reviews = [
    {
      avatar: "https://cdn.shopify.com/s/files/1/0642/8858/7843/files/girl_6.jpg?v=1751876349",
      text: "Bahut pyara product tha, packaging bhi zabardast thi!",
      name: "— Ayesha from Multan"
    },
    {
      avatar: "https://cdn.shopify.com/s/files/1/0642/8858/7843/files/girl_5.jpg?v=1751876349",
      text: "Sister ke liye gift lia tha, woh bohat khush hui.",
      name: "— Nida from Hyderabad"
    },
    {
      avatar: "https://cdn.shopify.com/s/files/1/0642/8858/7843/files/boy_3.jpg?v=1751876349",
      text: "Excellent quality and totally worth it.",
      name: "— Saad from Karachi"
    },
    {
      avatar: "https://cdn.shopify.com/s/files/1/0642/8858/7843/files/boy.jpg?v=1751876349",
      text: "Har dafa yahi se order karta hoon. Trusted!",
      name: "— Imran from Faisalabad"
    },
    {
      avatar: "https://cdn.shopify.com/s/files/1/0642/8858/7843/files/girl3.jpg?v=1751876349",
      text: "Fast delivery and super neat packaging!",
      name: "— Zara from Gujranwala"
    },
    {
      avatar: "https://cdn.shopify.com/s/files/1/0642/8858/7843/files/boy2.jpg?v=1751876349",
      text: "Mujhe laga fake hoga, lekin bohat acha nikla!",
      name: "— Bilal from Sialkot"
    },
    {
      avatar: "https://cdn.shopify.com/s/files/1/0642/8858/7843/files/boy4.jpg?v=1751876349",
      text: "Achha experience tha, return policy bhi clear thi.",
      name: "— Usman from Bahawalpur"
    },
    {
      avatar: "https://cdn.shopify.com/s/files/1/0642/8858/7843/files/girl4.jpg?v=1751876349",
      text: "Main sab doston ko yahi suggest karti hoon!",
      name: "— Hina from Sukkur"
    },
    {
      avatar: "https://cdn.shopify.com/s/files/1/0642/8858/7843/files/girl2.jpg?v=1751876349",
      text: "Jo chaha tha wohi mila, bohat khushi hui!",
      name: "— Rabia from Larkana"
    },
    {
      avatar: "https://cdn.shopify.com/s/files/1/0642/8858/7843/files/girl.jpg?v=1751876350",
      text: "Ma sha Allah, zabardast service and item dono!",
      name: "— Kiran from Lahore"
    }
  ];

  let currentIndex = 0;

  function renderReview(index) {
    const avatarStack = document.getElementById("avatar-stack");
    const reviewBox = document.getElementById("review-box");
    const reviewText = document.getElementById("review-text");
    const reviewName = document.getElementById("review-name");

    if (!avatarStack || !reviewBox || !reviewText || !reviewName) return;

    const total = reviews.length;
    const i1 = index % total;
    const i2 = (index + 1) % total;
    const i3 = (index + 2) % total;

    avatarStack.innerHTML = `
      <img src="${reviews[i1].avatar}" alt="avatar" class="active">
      <img src="${reviews[i2].avatar}" alt="avatar" class="second">
      <img src="${reviews[i3].avatar}" alt="avatar" class="third">
    `;

    reviewBox.classList.remove("visible");
    setTimeout(() => {
      reviewText.textContent = reviews[i1].text;
      reviewName.textContent = reviews[i1].name;
      reviewBox.classList.add("visible");
    }, 300);
  }

  const reviewBox = document.getElementById("review-box");
  if (reviewBox) {
    renderReview(currentIndex);
    setInterval(() => {
      currentIndex = (currentIndex + 1) % reviews.length;
      renderReview(currentIndex);
    }, 6000);
  }
}

/* ── Cash on Delivery Form and Modal Logic ── */
function initCODModal() {
  const codBtn = document.getElementById('cod-order-btn');
  const codModal = document.getElementById('cod-modal');
  const closeCod = document.getElementById('close-cod-modal');
  const codForm = document.getElementById('cod-order-form');

  if (!codBtn || !codModal || !closeCod || !codForm) return;

  codBtn.addEventListener('click', () => {
    const qtyInput = document.getElementById('qtyInput');
    const selectedVariantIdInput = document.getElementById('product-selected-variant-id');
    
    const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    const variantId = selectedVariantIdInput ? selectedVariantIdInput.value : '';
    
    if (!variantId) {
      alert('Please select variant options first.');
      return;
    }

    const activeBtns = Array.from(document.querySelectorAll('.variant-group')).map(group => group.querySelector('.variant-btn.active'));
    const selectedOptionsText = activeBtns.map(btn => btn ? btn.dataset.value : '').filter(val => val !== '').join(' / ');
    
    const summaryVariantTitle = document.getElementById('cod-summary-variant-title');
    if (summaryVariantTitle) {
      summaryVariantTitle.textContent = selectedOptionsText || 'Default Variant';
    }
    
    const summaryQty = document.getElementById('cod-summary-qty');
    if (summaryQty) {
      summaryQty.textContent = `Qty: ${qty}`;
    }
    
    const priceCurrent = document.querySelector('.price-current');
    const summaryPrice = document.getElementById('cod-summary-price');
    if (summaryPrice && priceCurrent) {
      summaryPrice.textContent = priceCurrent.textContent;
    }
    
    const mainImg = document.getElementById('mainImg');
    const summaryImg = document.getElementById('cod-summary-image');
    if (summaryImg && mainImg) {
      summaryImg.src = mainImg.src;
    }
    
    codModal.classList.add('active');
  });

  const hideCod = () => { codModal.classList.remove('active'); };
  closeCod.addEventListener('click', hideCod);
  codModal.addEventListener('click', (e) => {
    if (e.target === codModal) hideCod();
  });

  codForm.addEventListener('submit', (e) => {
    e.preventDefault();
    
    const name = document.getElementById('cod-name').value;
    const phone = document.getElementById('cod-phone').value;
    const address = document.getElementById('cod-address').value;
    const city = document.getElementById('cod-city').value;
    
    const qtyInput = document.getElementById('qtyInput');
    const selectedVariantIdInput = document.getElementById('product-selected-variant-id');
    const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    const variantId = selectedVariantIdInput ? selectedVariantIdInput.value : '';

    if (!variantId) {
      alert('Please select variant options first.');
      return;
    }

    const checkoutUrl = `/cart/${variantId}:${qty}?checkout[shipping_address][first_name]=${encodeURIComponent(name)}&checkout[shipping_address][phone]=${encodeURIComponent(phone)}&checkout[shipping_address][address1]=${encodeURIComponent(address)}&checkout[shipping_address][city]=${encodeURIComponent(city)}`;
    window.location.href = checkoutUrl;
  });
}

/* ── Click-outside close handler for size guide modal ── */
function initSizeGuideOutsideClick() {
  const modal = document.getElementById('size-chart-modal');
  if (modal) {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.classList.remove('active');
    });
  }
}

/* ── Video section data reader (reads Liquid data attrs → JS config) ── */


/* ═══════════════════════════════════════════════════════════════════
   BOOT
   ═══════════════════════════════════════════════════════════════════ */
document.addEventListener('DOMContentLoaded', () => {
  // 1. Preloader (immediately)
  if (TC.enablePreloader !== false) initPreloader();

  // 2. WebGL Galaxy — Three.js now loads lazily (after page load), so wait
  //    for it to be ready instead of giving up immediately.
  if (TC.enableGalaxy !== false) {
    (function waitForThree(tries) {
      if (typeof THREE !== 'undefined') { initGalaxy(); return; }
      if (tries > 40) {  // ~8s max, then give up quietly
        const c = document.getElementById('glx-canvas');
        if (c) c.style.display = 'none';
        return;
      }
      setTimeout(function () { waitForThree(tries + 1); }, 200);
    })(0);
  } else {
    const canvas = document.getElementById('glx-canvas');
    if (canvas) canvas.style.display = 'none';
  }

  // 3. Lenis smooth scroll — DESKTOP ONLY (Delegated to unified initLenis engine)
  var _isTouch = window.matchMedia('(hover: none)').matches || window.innerWidth <= 768;
  if (TC.enableLenis !== false && !_isTouch) {
    if (typeof window.initLenis === 'function') {
      window.initLenis();
    }
    (function waitForLenis(tries) {
      if (window._lenis) return;
      if (typeof Lenis !== 'undefined') {
        if (typeof window.initLenis === 'function') window.initLenis();
        return;
      }
      if (tries > 30) {
        window.addEventListener('scroll', function () { handleGlobalScroll(window.scrollY); }, { passive: true });
        return;
      }
      setTimeout(function () { waitForLenis(tries + 1); }, 200);
    })(0);
  } else {
    window.addEventListener('scroll', function () { handleGlobalScroll(window.scrollY); }, { passive: true });
  }

  // 3b. Third-party modals (e.g. the AG Product Reviews "write a review"
  //     popup) lock scrolling by setting body { overflow: hidden }, but
  //     Lenis drives its own transform-based scroll that ignores native
  //     overflow — so the page kept scrolling behind an open modal. Watch
  //     for that class/style change and pause/resume Lenis to match.
  (function watchModalScrollLock() {
    var wasHidden = false;
    function syncLenis() {
      var isHidden = getComputedStyle(document.body).overflow === 'hidden';
      if (isHidden === wasHidden) return;
      wasHidden = isHidden;
      var lenis = window.__lenis || window._lenis;
      if (!lenis) return;
      if (isHidden) lenis.stop(); else lenis.start();
    }
    new MutationObserver(syncLenis).observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] });
  })();

  // 4. Scroll reveal
  if (TC.enableReveal !== false) {
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed', 'm-revealed');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

    document.querySelectorAll('.m-reveal, .anim-fade-up, .anim-blur, .anim-scale').forEach(el => {
      revealObserver.observe(el);
    });
    window.__revealObserver = revealObserver;
  }

  // 5. Cart count sync
  syncCartCount();

  // 6. UI subsystems
  initAccordions();
  initSliderProgress();
  enhanceSearchOverlay();

  // 7. Premium video sections (3D Coverflow and Floating video cards)
  if (document.querySelector('[id^="wheel-carousel-"]') ||
      document.querySelector('[id^="floating-cards-container-"]') ||
      document.getElementById('split-showcase-container') ||
      document.getElementById('fullwidth-video-container')) {
    initPremiumVideoSections();
  }

  // 8. Category Circles Slider
  initCategorySlider();

  // 9. Product page
  if (document.getElementById('main-product')) {
    initProductPage();
    initProductForm();
    initProductGallery();
    initQtyAdjuster();
    initVariantSelectors();
    initWishlistDetailBtn();
    initShippingDates();
    initSizeChartModal();
    initStickyMobileBar();
    initReviewsWidget();
    initCODModal();
    initSizeGuideOutsideClick();
  }

  // Init wishlist active states on load
  (function() {
    const list = Wishlist.getAll();
    const ids = new Set(list.map(i => i.id));
    document.querySelectorAll('.pcard-wishlist-btn[data-wishlist-id]').forEach(btn => {
      if (ids.has(String(btn.dataset.wishlistId))) {
        btn.classList.add('active');
        const svg = btn.querySelector('svg');
        if (svg) {
          svg.style.fill = 'var(--gold)';
          svg.style.stroke = 'var(--gold)';
        }
      }
    });
  })();

  // 19. Quick-add size buttons (product cards)

  // 10. Collection page
  if (document.getElementById('collections-layout')) {
    initCollectionsPage();
  }

  // 12. Hero carousel (init from JS for autoplay + dots)
  const heroSlider = document.querySelector('.hero-slider');
  if (heroSlider) {
    const slides = heroSlider.querySelectorAll('.slide');
    const dots = document.querySelectorAll('.slider-dots .dot');
    if (slides.length > 1) {
      let current = 0;
      const speed = parseInt(heroSlider.closest('.section')?.dataset?.autoplaySpeed || '5000', 10);

      const goTo = idx => {
        slides[current].classList.remove('active');
        slides[current].setAttribute('aria-hidden', 'true');
        dots[current]?.classList.remove('active');
        dots[current]?.setAttribute('aria-selected', 'false');
        current = (idx + slides.length) % slides.length;
        slides[current].classList.add('active');
        slides[current].setAttribute('aria-hidden', 'false');
        dots[current]?.classList.add('active');
        dots[current]?.setAttribute('aria-selected', 'true');
      };

      const autoplay = setInterval(() => goTo(current + 1), speed);

      /* Touch swipe on mobile — autoplay keeps running alongside */
      let heroSwipeX = 0;
      heroSlider.addEventListener('touchstart', e => {
        heroSwipeX = e.touches[0].clientX;
      }, { passive: true });
      heroSlider.addEventListener('touchend', e => {
        const diff = e.changedTouches[0].clientX - heroSwipeX;
        if (Math.abs(diff) > 45) {
          if (diff < 0) goTo(current + 1);
          else goTo(current - 1);
        }
      }, { passive: true });

      dots.forEach(dot => {
        dot.addEventListener('click', () => {
          clearInterval(autoplay);
          goTo(parseInt(dot.dataset.index, 10));
        });
        dot.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            clearInterval(autoplay);
            goTo(parseInt(dot.dataset.index, 10));
          }
        });
      });
    }
  }

  // 13. Tab switching (featured collections) - Handled in main tab initializer block (Section 2)


  // 14. Filter sidebar toggle (collections page)
  const filterToggle = document.getElementById('filter-toggle-btn');
  const filterSidebar = document.getElementById('filter-sidebar');
  const closeFilters = document.getElementById('close-filters');
  if (filterToggle && filterSidebar) {
    const toggleFilter = () => {
      const isOpen = filterSidebar.classList.toggle('is-open');
      filterToggle.setAttribute('aria-expanded', isOpen);
      document.body.classList.toggle('filter-open', isOpen);
    };
    filterToggle.addEventListener('click', toggleFilter);
    if (closeFilters) closeFilters.addEventListener('click', () => {
      filterSidebar.classList.remove('is-open');
      filterToggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('filter-open');
    });
  }

  // 15. View selector (grid columns)
  document.querySelectorAll('.view-btn').forEach(btn => {
    btn.addEventListener('click', function () {
      const grid = document.getElementById('catalog-grid');
      if (!grid) return;
      document.querySelectorAll('.view-btn').forEach(b => b.classList.remove('active'));
      this.classList.add('active');
      const cols = this.dataset.view;
      grid.className = grid.className.replace(/grid-view-\d+/g, '');
      grid.classList.add(`grid-view-${cols}`);
    });
  });

  // 16. Cart type routing — DISABLED. Cart icons are now plain <a href="/cart">
  //     links that navigate straight to the full cart page (no side drawer).
  document.addEventListener('click', e => {
    if (true) return;   // always let the link navigate
    const cartBtn = e.target.closest('#open-cart, .mobile-bottom-cart-btn, #mobile-bottom-cart-btn');
    if (!cartBtn) return;
    if (TC.cartType === 'page') {
      window.location.href = TC.routes?.cart_url || '/cart';
    } else {
      const drawer = document.getElementById('cart-drawer');
      if (drawer) {
        drawer.hidden = false;
        drawer.removeAttribute('hidden');
        document.body.classList.add('cart-open');
        renderCartDrawer();
      }
    }
  });

  // 17. Free shipping progress bar
  if (TC.cartShowFreeShipping) {
    ShopifyCart.getCart().then(cart => {
      const threshold = TC.freeShippingThreshold || 15000;
      const progress = Math.min(100, (cart.total_price / threshold) * 100);
      const progressBar = document.querySelector('.free-shipping-bar');
      if (progressBar) progressBar.style.width = `${progress}%`;
      const progressText = document.querySelector('.free-shipping-text');
      if (progressText) {
        const remaining = threshold - cart.total_price;
        if (remaining <= 0) {
          progressText.textContent = '🎉 You qualify for free shipping!';
        } else {
          const formatted = new Intl.NumberFormat('en-US', {
            style: 'currency', currency: TC.currency || 'USD'
          }).format(remaining / 100);
          progressText.textContent = `Add ${formatted} more for free shipping`;
        }
      }
    }).catch(() => { });
  }

  // 19. Quick-add size buttons (product cards) — in-stock sizes navigate to
  //     the product page (pre-selecting that size via ?variant=), out-of-
  //     stock sizes are disabled (struck through in CSS) and unclickable.
  document.addEventListener('click', e => {
    const sizeBtn = e.target.closest('.quick-size-btn');
    if (!sizeBtn || sizeBtn.disabled) return;
    e.preventDefault(); e.stopPropagation();
    const url = sizeBtn.dataset.productUrl;
    if (url) window.location.href = url;
  });

  // 20. pcard Buy Now — it's a plain <a href> to the product page now;
  //     let the browser navigate (no preventDefault, no cart add).

  // 21. pcard Quick Cart buttons — toast, no drawer
  document.addEventListener('click', e => {
    const quickBtn = e.target.closest('.pcard-quick-cart-btn');
    if (!quickBtn || quickBtn.disabled) return;
    const variantId = quickBtn.dataset.variantId;
    if (!variantId) return;
    ShopifyCart.addItem(variantId, 1).then(() => {
      biCartToast('Added to Cart!');
      updateCartCount();
    }).catch(err => console.warn('Quick cart failed:', err));
  });

  // 21b. Related-products carousel — native scroll, prev/next buttons.
  document.querySelectorAll('[data-related-nav]').forEach(nav => {
    const section = nav.closest('#related-products') || nav.parentElement;
    const track = section && section.querySelector('[data-related-track]');
    if (!track) return;
    const prevBtn = nav.querySelector('[data-related-prev]');
    const nextBtn = nav.querySelector('[data-related-next]');
    if (!prevBtn || !nextBtn) return;

    function scrollAmount() {
      const card = track.querySelector('.pcard');
      const gap = 24;
      return card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    }

    function updateButtons() {
      const maxScroll = track.scrollWidth - track.clientWidth - 2;
      prevBtn.disabled = track.scrollLeft <= 2;
      nextBtn.disabled = track.scrollLeft >= maxScroll;
    }

    prevBtn.addEventListener('click', () => {
      track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
    });
    nextBtn.addEventListener('click', () => {
      track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
    });
    track.addEventListener('scroll', () => {
      window.requestAnimationFrame(updateButtons);
    }, { passive: true });
    window.addEventListener('resize', updateButtons);
    updateButtons();
  });

  // 22. Recently Viewed — track on product pages
  const productTrackEl = document.querySelector('[data-product-id]');
  if (productTrackEl) {
    const pid = productTrackEl.dataset.productId;
    const ptitle = productTrackEl.dataset.productTitle;
    const pprice = productTrackEl.dataset.productPrice;
    const pimage = productTrackEl.dataset.productImage;
    if (pid) {
      let recent = JSON.parse(localStorage.getItem('bi_recently_viewed') || '[]');
      recent = recent.filter(p => String(p.id) !== String(pid));
      recent.unshift({ id: pid, title: ptitle, price: pprice, image: pimage, url: window.location.pathname });
      recent = recent.slice(0, 5);
      localStorage.setItem('bi_recently_viewed', JSON.stringify(recent));
    }
  }

  // 23. Recently Viewed — render in mobile nav drawer
  const recentSection = document.getElementById('mobile-nav-recent');
  const recentGrid = document.getElementById('mobile-nav-recent-grid');
  if (recentSection && recentGrid) {
    const recent = JSON.parse(localStorage.getItem('bi_recently_viewed') || '[]');
    if (recent.length > 0) {
      recentSection.style.display = 'block';
      recentGrid.innerHTML = recent.slice(0, 3).map(item => `
        <div class="menu-feat-item" role="button" tabindex="0" onclick="window.location.href='${item.url}'" onkeydown="if(event.key==='Enter')window.location.href='${item.url}'">
          <img src="${item.image}" alt="${item.title}" loading="lazy">
          <div class="menu-feat-info">
            <h5>${item.title}</h5>
            <span class="menu-feat-price">${item.price}</span>
          </div>
        </div>
      `).join('');
    }
  }

  // 24. Ripple micro-interaction on all .btn buttons
  document.addEventListener('click', e => {
    const btn = e.target.closest('.btn');
    if (!btn) return;
    const existing = btn.querySelector('.ripple-effect');
    if (existing) existing.remove();
    const rect = btn.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const ripple = document.createElement('span');
    ripple.className = 'ripple-effect';
    ripple.style.cssText = `
      position:absolute;border-radius:50%;
      width:${size}px;height:${size}px;
      left:${e.clientX - rect.left - size / 2}px;
      top:${e.clientY - rect.top - size / 2}px;
      background:rgba(255,255,255,0.25);
      transform:scale(0);animation:rippleAnim 0.6s ease-out forwards;
      pointer-events:none;
    `;
    btn.style.position = btn.style.position || 'relative';
    btn.style.overflow = 'hidden';
    btn.appendChild(ripple);
    setTimeout(() => { if (ripple.parentNode) ripple.remove(); }, 700);
  });

  // Handle Back-Forward Cache (bfcache) restore to prevent hidden sections
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      document.querySelectorAll('.reveal-on-scroll').forEach(el => {
        el.classList.add('revealed');
      });
    }
  });

  console.log('%c✦ Bin Ibrahim Theme Loaded', 'color:#D4AF37;font-size:14px;font-weight:bold;');
});

/* ── Isolated Mobile Drawer & Search Overlay Event Delegation ──
   Bound globally to document to run independently of DOMContentLoaded or other script failures */
(function() {
  // Move the drawer to be a direct child of <body>. The drawer uses
  // position:fixed, but it is rendered inside the navigation <section>
  // whose ancestors (relative positioning / will-change / backdrop-filter)
  // break fixed positioning — which made the drawer render as a small
  // clipped box near the header instead of a full-height side panel.
  // Re-parenting to <body> guarantees fixed is relative to the viewport.
  function ensureDrawerOnBody() {
    const drawer = document.getElementById('mobile-nav-drawer');
    if (drawer && drawer.parentElement !== document.body) {
      document.body.appendChild(drawer);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureDrawerOnBody);
  } else {
    ensureDrawerOnBody();
  }

  document.addEventListener('click', async (e) => {
    // 1. Mobile Bottom Menu / Header Burger Click
    const menuBtn = e.target.closest('#mobile-bottom-menu-btn') || e.target.closest('#open-mobile-menu');
    if (menuBtn) {
      e.preventDefault();
      ensureDrawerOnBody();
      const drawer = document.getElementById('mobile-nav-drawer');
      if (drawer) {
        // Clear any leftover inline transform so the .active CSS class alone
        // controls open/closed position (prevents a stuck / half-open drawer).
        drawer.style.transform = '';
        drawer.classList.toggle('active');
        document.body.classList.toggle('drawer-open', drawer.classList.contains('active'));
      }
      return;
    }

    // 2. Mobile Menu Close Button
    const closeMenuBtn = e.target.closest('#close-mobile-menu') || e.target.closest('.drawer-close-btn');
    if (closeMenuBtn && closeMenuBtn.closest('#mobile-nav-drawer')) {
      e.preventDefault();
      e.stopPropagation(); // Prevent any other click handlers
      const drawer = document.getElementById('mobile-nav-drawer');
      if (drawer) {
        drawer.classList.remove('active');
        // Clear inline transform so a later reopen (class toggle) is not blocked.
        drawer.style.transform = '';
        document.body.classList.remove('drawer-open');
      }
      // Force account icon to stay visible to fix vanishing bugs
      const accountIcon = document.querySelector('.header-right .account-trigger');
      if (accountIcon) {
        accountIcon.style.display = 'inline-flex';
        accountIcon.style.opacity = '1';
        accountIcon.style.visibility = 'visible';
      }
      return;
    }

    // 3. Mobile Bottom Search Click
    const searchBtn = e.target.closest('#mobile-bottom-search-btn');
    if (searchBtn) {
      e.preventDefault();
      const overlay = document.getElementById('search-overlay');
      if (overlay) {
        overlay.hidden = false;
        overlay.removeAttribute('hidden');
        overlay.classList.add('active');
        document.body.classList.add('overlay-open');
        const inp = document.getElementById('search-input-field');
        if (inp) setTimeout(() => inp.focus(), 50);
      }
      return;
    }

    // 4. Search Overlay Close Button
    const closeSearchBtn = e.target.closest('#close-search');
    if (closeSearchBtn) {
      e.preventDefault();
      const overlay = document.getElementById('search-overlay');
      if (overlay) {
        overlay.classList.remove('active');
        overlay.hidden = true;
        overlay.setAttribute('hidden', '');
        document.body.classList.remove('overlay-open');
      }
      return;
    }

    // 5. Mobile Bottom Cart — now a plain <a href="/cart"> link; let it
    //    navigate to the cart page (no drawer).

    // 6. Buy Now button on product card
    const buyNowBtn = e.target.closest('.pcard-add-btn[data-variant-id]');
    if (buyNowBtn) {
      e.preventDefault();
      const variantId = buyNowBtn.dataset.variantId;
      if (variantId) {
        await addToCartWithFeedback(variantId, 1, buyNowBtn);
      } else {
        const url = buyNowBtn.dataset.productUrl;
        if (url) window.location.href = url;
      }
      return;
    }

    // 7. Quick cart button on product card
    const quickCartBtn = e.target.closest('.pcard-quick-cart-btn[data-variant-id]');
    if (quickCartBtn) {
      e.preventDefault();
      e.stopPropagation();
      const variantId = quickCartBtn.dataset.variantId;
      if (variantId) {
        quickCartBtn.disabled = true;
        if (quickCartBtn.classList.contains('cart-added')) {
          fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: String(variantId), quantity: 0 })
          }).then(() => {
            quickCartBtn.classList.remove('cart-added');
            quickCartBtn.innerHTML = '<svg viewBox="0 0 24 24"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>';
            quickCartBtn.style.background = '';
            quickCartBtn.style.borderColor = '';
            if (window.updateCartCount) window.updateCartCount();
            if (window.biCartToast) window.biCartToast('Removed from Cart!');
            quickCartBtn.disabled = false;
          }).catch(() => { quickCartBtn.disabled = false; });
        } else {
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          }).then(() => {
            quickCartBtn.classList.add('cart-added');
            quickCartBtn.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#000" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg>';
            quickCartBtn.style.background = 'var(--gold)';
            quickCartBtn.style.borderColor = 'var(--gold)';
            if (window.updateCartCount) window.updateCartCount();
            if (window.biCartToast) window.biCartToast('Added to Cart!');
            quickCartBtn.disabled = false;
          }).catch(() => { quickCartBtn.disabled = false; });
        }
      }
      return;
    }

    // 8. Wishlist button (data-wishlist-id)
    const wishlistBtn = e.target.closest('[data-wishlist-id]');
    if (wishlistBtn) {
      // Allow the click if it's on a button or link styled for wishlist
      if (wishlistBtn.classList.contains('pcard-wishlist-btn') || wishlistBtn.id === 'wishlistDetailBtn' || wishlistBtn.tagName === 'BUTTON') {
        e.preventDefault();
        
        const product = {
          id: wishlistBtn.dataset.wishlistId,
          title: wishlistBtn.dataset.wishlistTitle || 'Product',
          price: wishlistBtn.dataset.wishlistPrice || '',
          image: wishlistBtn.dataset.wishlistImage || '',
          url: wishlistBtn.dataset.wishlistUrl || ''
        };
        
        if (typeof Wishlist !== 'undefined') {
          const added = Wishlist.toggle(product);

          // Toast Popup Notification
          let toast = document.getElementById('wishlist-toast');
          if (!toast) {
            toast = document.createElement('div');
            toast.id = 'wishlist-toast';
            Object.assign(toast.style, {
              position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%) translateY(100px)',
              background: 'rgba(2, 2, 8, 0.95)', color: '#fff', padding: '12px 24px', borderRadius: '50px',
              fontSize: '0.9rem', border: '1px solid var(--gold)', zIndex: '9999',
              boxShadow: '0 10px 25px rgba(0,0,0,0.5)', transition: 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              display: 'flex', alignItems: 'center', gap: '10px', pointerEvents: 'none'
            });
            document.body.appendChild(toast);
          }
          
          toast.innerHTML = added 
            ? `<svg width="18" height="18" viewBox="0 0 24 24" fill="var(--gold)" stroke="var(--gold)" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg> Added to Wishlist!`
            : `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg> Removed from Wishlist`;
            
          // Animate in
          toast.style.transform = 'translateX(-50%) translateY(0)';
          
          // Clear previous timeout and animate out
          if (window.wishlistToastTimeout) clearTimeout(window.wishlistToastTimeout);
          window.wishlistToastTimeout = setTimeout(() => {
            toast.style.transform = 'translateX(-50%) translateY(100px)';
          }, 3000);
        }
        return;
      }
    }
  });
})();

/* ── Video loading overlay ─────────────────────────────────────────────
   Every <video> gets a gold spinner overlay (Bin Ibrahim style) until it
   can actually play, so sections never look stuck while media downloads. */
(function () {
  function attachLoaders() {
    document.querySelectorAll('video').forEach(function (v) {
      if (v._biLoaderDone) return;
      var parent = v.parentElement;
      if (!parent || parent.querySelector('.vid-loader')) return;
      if (v.readyState >= 3) { v._biLoaderDone = true; return; } // already playable
      v._biLoaderDone = true;
      if (getComputedStyle(parent).position === 'static') parent.style.position = 'relative';
      var l = document.createElement('div');
      l.className = 'vid-loader';
      l.innerHTML = '<div class="vid-loader__ring"></div>';
      parent.appendChild(l);
      var done = function () {
        l.classList.add('vid-loader--done');
        setTimeout(function () { l.remove(); }, 450);
      };
      ['canplay', 'playing', 'loadeddata'].forEach(function (evt) {
        v.addEventListener(evt, done, { once: true });
      });
      setTimeout(done, 10000); // safety: never keep a spinner forever
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachLoaders);
  } else { attachLoaders(); }
  // Re-scan after load + a beat later (some videos initialise late)
  window.addEventListener('load', attachLoaders);
  setTimeout(attachLoaders, 1500);
})();
